import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import {
  Row, Col, Form, Button, Alert, Spinner,
} from "react-bootstrap";
import { FaPlus, FaCheckCircle, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import AutocompleteField from "./components/AutocompleteField";
import SearchableSelect from "./components/SearchableSelect";
import { getUser, isAdminOrDistrRole } from "./utils/auth";
import {
  territoriesDescriptor,
  salesRepsDescriptor,
  customersDescriptor,
  systemsDescriptor,
  warm as warmRefData,
  load as loadRefData,
  peek as peekRefData,
  subscribe as subscribeRefData,
} from "./utils/refDataCache";
import {
  AUTOCOMPLETE_TERRITORY,
  buildSalesRepConfig,
  buildCustomerConfig,
  isAdminTerritory,
  isCustomerServiceRole,
} from "./utils/refDataConfigs";
import { REPORT_STYLES, DEFAULT_REPORT_STYLE } from "./utils/quoteReports";
import { estTotalAtOrder } from "./utils/quoteTotals";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// -----------------------------------------------------------------------------
// Plan catalogue + commission tier defaults — mirrors Sections 4 + 9e of
// SalesQuote_UI_Developer_Guide.docx.
// -----------------------------------------------------------------------------
export const PLAN_CATEGORIES = [
  { code: "N30", label: "Net 30",         tagline: "Pay in full within 30 days. No fees." },
  { code: "POT", label: "Pay Over Time",  tagline: "Split into 2–36 monthly payments." },
  { code: "BK",  label: "Balanced Split", tagline: "Same-day split into multiple invoices." },
  { code: "CTO", label: "Consign-to-Own", tagline: "Lease-to-own with a monthly ceiling." },
  { code: "MSP", label: "Multi-Shipment", tagline: "One system split into deliveries — each ordered on its own PO." },
];

// Multi-shipment sizing bounds (mirrors the API: 2–36 shipments, or a
// per-shipment dollar ceiling the planner sizes against).
export const MSP_MIN_SHIPMENTS = 2;
export const MSP_MAX_SHIPMENTS = 36;

export const CTO_NAMED_PLANS = ["C1K","C2K","C3K","C4K","C5K","C6K","C7K","C8K","C9K","C10K"];
export const BK_QUICK_PICKS  = [2000, 3000, 5000, 8000];
// Balanced Split quick-pick slider — per-invoice cap from $1,000 to $9,000 in
// $1,000 steps (replaces the old discrete radio quick-picks).
export const BK_SLIDER_MIN   = 1000;
export const BK_SLIDER_MAX   = 9000;
export const BK_SLIDER_STEP  = 1000;

export const DEFAULT_COMMISSION_TIERS = [
  { minDiscountPercent: 0,  maxDiscountPercent: 0,  discountFactor: 1.00, tierName: "No Discount" },
  { minDiscountPercent: 1,  maxDiscountPercent: 5,  discountFactor: 0.90, tierName: "Small" },
  { minDiscountPercent: 6,  maxDiscountPercent: 10, discountFactor: 0.75, tierName: "Medium" },
  { minDiscountPercent: 11, maxDiscountPercent: 15, discountFactor: 0.50, tierName: "Large" },
  { minDiscountPercent: 16, maxDiscountPercent: 20, discountFactor: 0.25, tierName: "Max" },
];

export function tierForDiscount(tiers, pct) {
  const p = Number(pct) || 0;
  return tiers.find((t) =>
    p >= Number(t.minDiscountPercent) && p <= Number(t.maxDiscountPercent)
  ) || tiers[0];
}

export function authHeaders() {
  const h = { "Content-Type": "application/json" };
  const t = sessionStorage.getItem("authToken");
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

// -----------------------------------------------------------------------------
// Logged-in user's territory scope, used to pre-filter the customer
// autocomplete when no Sales Rep or Territory is picked yet. D99 Admin /
// Distributor sees everything; everyone else is scoped to their own territory
// subtree (codes that start with "{territoryId}-").
// -----------------------------------------------------------------------------
function getCurrentUserScope() {
  const role = (sessionStorage.getItem("userRole") || "").toLowerCase();
  const territoryId = sessionStorage.getItem("territoryId") || "";
  const isD99 = territoryId.toLowerCase() === "d99";
  return {
    role,
    territoryId,
    // SAL-GAP-62 (2026-10-21): Customer Service works company-wide — its own
    // territory is not pre-filled and does not narrow the rep / customer
    // steps ("Reps in D03…"); it may pick a territory or skip, like D99.
    isUnrestricted: (isAdminOrDistrRole(role) && isD99) || isCustomerServiceRole(role),
  };
}

// Number of options in a preloaded list, or null if it isn't cached yet.
function optionCount(key) {
  const list = peekRefData(key);
  return list ? list.length : null;
}

// Territory / Sales Rep / Customer autocomplete configs now live in the shared
// utils/refDataConfigs module (imported above) so every page populates and
// behaves identically. isAdminTerritory is also imported from there.

// Part Number autocomplete — unchanged.
// ─────────────────────────────────────────────────────────────────────────────
// "Individual components" — the escape hatch out of system-first quoting.
//
// A rep who needs reorder parts but does NOT know which system they belong to
// picks this pinned row at the top of the System dropdown. The line then holds
// this sentinel as its partNum, quantity 0 and reorder true — i.e. it IS a
// reorder-only line, so isReorderOnly / selectedReorderBoms / the submit builder
// all keep working untouched. The only thing that branches is where the parts
// list comes from: /api/Part/reorder-parts/search (every system) instead of
// /api/Part/{partNum} (one system's BOM).
//
// The sentinel never reaches the wire — the submit builder drops the system line
// for any reorder-only line (see buildLines).
const INDIVIDUAL_PARTS = "__INDIVIDUAL__";
export const isIndividualParts = (line) => line?.partNum === INDIVIDUAL_PARTS;

// The pinned row itself. `__individual` is the marker the select handler reads;
// the display/secondary fields are what the rep sees in the list.
const INDIVIDUAL_OPTION = {
  __individual: true,
  Part_PartNum: "Individual components",
  Part_PartDescription: "quote reorder parts on their own — search every system",
};

const AUTOCOMPLETE_PART = {
  apiUrl: "/api/Part/systems/search",
  queryParam: "q",
  dataPath: "systems",
  displayField: "Part_PartNum",
  valueField: "Part_PartNum",
  secondaryField: "Part_PartDescription",
  minChars: 1,
  defaultParams: "top=10",
  // Preloaded searchable dropdown — the full active-system list is cached once
  // per session (loaded from /api/Part/systems, which returns all systems).
  preload: true,
  descriptor: systemsDescriptor(),
};

// -----------------------------------------------------------------------------
// Empty line factory.
// -----------------------------------------------------------------------------
let _lineSeq = 0;
function makeEmptyLine() {
  _lineSeq += 1;
  return {
    _key: _lineSeq,
    partNum: "",
    quantity: 1,
    repDiscountPercent: 0,
    paymentPlan: { planCode: "N30" },
    // Multi-shipment sizing mode: "count" (split into N deliveries) or
    // "ceiling" (no more than $X per shipment). Exactly one travels to the API.
    mspMode: "count",
    // Multi-shipment layout: one Epicor quote with N kit lines vs. one quote
    // per shipment, and one PDF per shipment when the quote is emailed.
    mspSeparateQuotes: false,
    mspSeparateDocs: false,
    potMode: "months",
    bkMode: "quick",
    ctoMode: "named",
    tradeIn: null,
    shippingAmount: null,
    miscChargesAmount: null,
    preview: null,
    previewError: null,
    previewLoading: false,
    // Price-contract verdict for this line, lifted off the preview response.
    // A blocked line is not addable and not submittable — see contractVerdict.
    contractNote: null,
    contractBlocks: false,
    // Set by the Review re-check when today's preview prices the line
    // differently from the one it was added with (UI-QTE-101 follow-up).
    priceChangeNote: null,
    // Reorder parts — when on, the system's BOM components can be added as
    // extra N30 lines grouped under the system. (Forces the whole line group
    // to Net 30.)
    reorder: false,
    reorderBoms: [],          // [{ partNum, bomType, qtyPer, unitPrice, quantity, selected }]
    reorderLoading: false,
    reorderError: null,
  };
}

// Build the PaymentPlanInput object sent to the API. Strips fields that
// don't apply to the selected plan. The category (line.paymentPlan.planCode)
// is "N30" / "POT" / "BK" / "CTO" — for CTO we check line.ctoMode to know
// whether the rep is in named-plan mode (C1K..C10K, planCode rewritten by
// effectivePlanCode) or custom-monthly mode (planCode="CTOC").
function buildPaymentPlan(line) {
  const category = line.paymentPlan.planCode;
  const out = { planCode: category };

  if (category === "POT") {
    if (line.potMode === "months" && line.paymentPlan.numberOfMonths) {
      out.numberOfMonths = Number(line.paymentPlan.numberOfMonths);
    } else if (line.potMode === "budget" && line.paymentPlan.desiredPerInvoice) {
      out.desiredPerInvoice = Number(line.paymentPlan.desiredPerInvoice);
    }
  }
  if (category === "BK" && line.paymentPlan.desiredPerInvoice) {
    out.desiredPerInvoice = Number(line.paymentPlan.desiredPerInvoice);
  }
  if (category === "CTO" && line.ctoMode === "custom" && line.paymentPlan.customMonthlyAmount) {
    out.customMonthlyAmount = Number(line.paymentPlan.customMonthlyAmount);
  }
  return out;
}

function effectivePlanCode(line) {
  const cat = line.paymentPlan.planCode;
  if (cat === "CTO") {
    return line.ctoMode === "custom" ? "CTOC" : (line.paymentPlan.namedCtoPlan || "C3K");
  }
  return cat;
}

// What a CTO slider floor was priced for: the preview's net amount depends on the part
// and the quantity, so a floor from another part or quantity is ignored (UI-QTE-084).
function ctoMinKeyOf(line) {
  return `${line.partNum || ""}|${Number(line.quantity) || 1}`;
}

function planCategory(line) {
  return line.paymentPlan.planCode;
}

// Reorder parts (BOM components) selected under a system line.
function selectedReorderBoms(line) {
  if (!line.reorder) return [];
  return (line.reorderBoms || []).filter((b) => b.selected && b.partNum);
}
// Cart-side price estimate for the selected reorder parts (unitPrice × qty).
// The server prices them authoritatively on submit.
// Extended amount for one reorder part — unit x qty. The `|| 1` matches the
// submit builder exactly (see buildLines), so the figure the rep reads on the
// row is the figure the server will bill. It used to be `|| 0` in the summary
// only, which meant a cleared quantity box showed $0.00 for a part that would
// still be billed at 1.
function bomExtended(b) {
  return (Number(b.unitPrice) || 0) * (Number(b.quantity) || 1);
}
function reorderEstimate(line) {
  return selectedReorderBoms(line).reduce((s, b) => s + bomExtended(b), 0);
}

// A line whose system quantity is exactly 0 is a "reorder parts only" line: the
// system itself is NOT quoted — only the selected reorder (BOM) parts are sent
// as Net-30 lines. A blank/empty quantity is NOT treated as reorder-only (it's
// mid-edit and defaults to qty 1), so an emptied field never silently drops the
// system from the quote.
export function isReorderOnly(line) {
  const q = line?.quantity;
  if (q === "" || q === null || q === undefined) return false;
  return Number(q) === 0;
}

// The quantity sent to the API for a line or reorder part: a whole number of at
// least 1. `Number(-1) || 1` stayed -1, and Epicor turned a -1 line into a
// quantity-1, $0 line (a free system). The box refuses a negative entry and the
// API refuses anything below 1; this is the last guard on the wire.
export function sendQty(q) {
  const n = Math.trunc(Number(q));
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

// A negative quantity on the line or on a picked reorder part. Add to cart stays
// disabled while one is on screen. Exactly 0 on the system is NOT negative: it is
// the "reorder parts only" mode above.
export function hasNegativeQuantity(line) {
  if (line?.quantity !== "" && line?.quantity != null && Number(line.quantity) < 0) return true;
  return (line?.reorderBoms || []).some((b) => b.selected && Number(b.quantity) < 0);
}

// UI-QR-N073 (2026-11-01): 1.5 systems was accepted here - the preview invented a
// $6,583.33 unit price, the cart said "qty 1.5" and the quote would save 1. Systems
// come in whole numbers; the box refuses a fraction and this keeps Add to cart off.
export function hasFractionalQuantity(line) {
  return line?.quantity !== "" && line?.quantity != null && !Number.isInteger(Number(line.quantity));
}

// UI-QR-N005 / N006 / N008 (2026-11-01): typed plan amounts the API refuses or misreads
// (a -$2,500 CTO ceiling, -$60 shipping, a -$3,000 budget, 2.5 shipments). Returns the
// plain message shown on the line, or null; Add to cart stays off while there is one.
export function planAmountProblem(line) {
  const cat = planCategory(line);
  const pp = line?.paymentPlan || {};
  const typed = (v) => v !== "" && v != null;
  if (cat === "POT" && line.potMode === "budget" && typed(pp.desiredPerInvoice) && Number(pp.desiredPerInvoice) < 0) {
    return "The customer's monthly budget must be more than $0.";
  }
  if (cat === "CTO") {
    if (line.ctoMode === "custom" && typed(pp.customMonthlyAmount) && Number(pp.customMonthlyAmount) < 0) {
      return "The monthly ceiling must be more than $0.";
    }
    if (typed(line.shippingAmount) && Number(line.shippingAmount) < 0) return "Shipping can't be negative. Enter 0 or more.";
    if (typed(line.miscChargesAmount) && Number(line.miscChargesAmount) < 0) return "Misc charges can't be negative. Enter 0 or more.";
  }
  if (cat === "MSP") {
    if (line.mspMode === "ceiling") {
      if (typed(pp.perShipmentCeiling) && !(Number(pp.perShipmentCeiling) > 0)) return "The ceiling per shipment must be more than $0.";
    } else if (typed(pp.shipmentCount)) {
      const n = Number(pp.shipmentCount);
      if (!Number.isInteger(n) || n < MSP_MIN_SHIPMENTS) {
        return `The number of shipments must be a whole number, ${MSP_MIN_SHIPMENTS} or more.`;
      }
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Line scope — what a system line actually quotes. Two answers and only two.
//
// Scope is DERIVED from the quantity, not stored. There is deliberately no
// `partsOnly` boolean on the draft: a flag sitting beside a NON-zero quantity
// would make isReorderOnly false while the UI said "Parts only", and the submit
// builder drops the system line on isReorderOnly alone (see buildLines) — so the
// system would be quoted for real, at full price, with nothing on screen saying
// so. Keeping isReorderOnly the single reader of this state is the whole safety
// story; every door into parts-only writes the same patch setLineScope writes.
// "Reorder parts" is the name the whole screen uses — the checkbox, the picker,
// the hint text and the Epicor-side concept. Never shorten it to "parts" in a
// label: on this screen "parts" could equally mean the system part itself.
export const LINE_SCOPE = {
  SYSTEM_AND_REORDER_PARTS: "system+reorder-parts", // quote the system AND the reorder parts picked under it
  REORDER_PARTS_ONLY: "reorder-parts-only",         // quote only the reorder parts; the system isn't added
};

export function lineScope(line) {
  return isReorderOnly(line) ? LINE_SCOPE.REORDER_PARTS_ONLY : LINE_SCOPE.SYSTEM_AND_REORDER_PARTS;
}

// The quantity a line returns to when it stops quoting parts only. Nothing else
// caches it, so without the stash a rep who had qty 3, flipped to parts-only and
// flipped back would silently land on 1.
export function restoredSystemQty(line) {
  const stashed = Number(line?.systemQtyBeforePartsOnly);
  return stashed > 0 ? line.systemQtyBeforePartsOnly : 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// Price contract — the preview's verdict for one line.
//
// `contractNote` / `contractBlocks` ride on the preview RESPONSE beside
// `pricing`, not inside it, so they are kept on the LINE rather than folded
// into line.preview: a refused line comes back with success=false and no
// pricing at all, and the sentence explaining why still has to reach the rep.
// Pass the parsed body on success, the thrown error on failure (the preview
// fetch copies them onto it), or null to clear.
// ─────────────────────────────────────────────────────────────────────────────
function contractVerdict(src) {
  return {
    contractNote: (src && src.contractNote) || null,
    contractBlocks: !!(src && src.contractBlocks),
  };
}

// The /api/Sales/preview body for one line — the draft preview and the Review
// step's contract re-check (UI-QTE-101) send the same thing, so the re-check
// gets the verdict the draft got, for today's contract state.
function salesPreviewBody(line, salesRepCode, custID) {
  const planCode = effectivePlanCode(line);
  const paymentPlan = { ...buildPaymentPlan(line), planCode };

  // Only forward a trade-in when it's actually filled in (partNum is the
  // required field). Sending an empty {partNum:"",...} object causes the
  // Epicor preview to fail validation.
  const validTradeIn =
    planCategory(line) === "N30" && line.tradeIn && line.tradeIn.partNum
      ? line.tradeIn
      : null;

  return {
    partNum: line.partNum,
    quantity: sendQty(line.quantity),
    paymentPlan,
    repDiscountPercent: Number(line.repDiscountPercent) || 0,
    tradeIn: validTradeIn,
    // The two Consign-to-Own boxes (Shipping / Misc) travel on the preview exactly as
    // they travel on create below, so the card shows the monthly and the total the
    // finished quote will carry (Issue 37). Null on every other plan - the API ignores
    // them there, as create does.
    shippingAmount: planCategory(line) === "CTO" && line.shippingAmount ? Number(line.shippingAmount) : null,
    miscChargesAmount: planCategory(line) === "CTO" && line.miscChargesAmount ? Number(line.miscChargesAmount) : null,
    salesRepCode,
    custID,
  };
}

// The submit refuses a contract-blocked line for the same reason the preview
// did (CreateSalesQuoteHandler throws on decision.MustRefuse), so the line is
// stopped here — not warned about — rather than after a hospital has been
// quoted a number nobody can honour.
function isContractBlocked(line) {
  return !!line && line.contractBlocks === true;
}

// The line's pre-tax system figure — the number CartLineRow heads the line
// with and the cart total adds up. Null when the line has no priced preview.
function preTaxLinePrice(preview) {
  if (!preview || preview.totalPrice == null) return null;
  return (Number(preview.totalPrice) || 0) - (Number(preview.taxAmount) || 0);
}

// Epicor could not price the part at all. Kapil 2026-10-23 (SAL-GAP-84): keep the
// yellow warning but do not let the line into the cart until it is priced.
function isUnpriced(line) {
  return !!line && !!line.previewError && /could not resolve pricing|resolve pricing/i.test(String(line.previewError));
}

// UI-QR-N107: a plan the preview refused for running past the 36-month cap (a CTO
// monthly typed under the minimum) is refused by create too, so it stays out of the cart.
export function isOverTermCap(line) {
  return !!line && !!line.previewError
    && /36-month|Minimum custom CTO payment/i.test(String(line.previewError));
}

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(2)}%`;
}

// =============================================================================
// Wizard step sequence — mirrors QuoteCreation_UI_Simulator.html.
// =============================================================================
// Territory / Sales Rep / Customer / Contacts are one combined step now —
// each section reveals below the previous one as it's answered.
const WIZARD_STEPS = [
  { id: "setup",   label: "Customer & Contacts" },
  { id: "addLine", label: "Add Line" },
  { id: "review",  label: "Review & Submit" },
];

// A fresh draft line for the Add-Line step (everything renders on one screen
// now — the product pick reveals the buying-option radios + plan details).
function newDraft() {
  return makeEmptyLine();
}

// =============================================================================
// QuoteCreate — guided wizard aligned to the Quote Creation Simulator.
//   territory → salesRep → customer → contacts → addLine (sub-wizard)
//   → review & submit (cart + submit merged) → (convert to sales order)
// =============================================================================
function QuoteCreate() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  // ── User scope (memoised — JWT doesn't change mid-session) ──────────────
  const userScope = useMemo(() => getCurrentUserScope(), []);

  // ── Step state ──────────────────────────────────────────────────────────
  // The wizard opens on the combined setup step (territory → rep → customer →
  // contacts revealed progressively); single-option lists are auto-selected by
  // the effects below, which auto-reveals the next section.
  const [step, setStep] = useState("setup");
  // Unrestricted (D99 admin) users may search reps across all territories —
  // this flag reveals the Sales Rep section without a territory pick.
  const [territorySkipped, setTerritorySkipped] = useState(false);

  // ── Header state ────────────────────────────────────────────────────────
  // All header fields start blank — the user must explicitly pick a
  // territory, rep, and customer (or pick a customer first and let the
  // upstream fields auto-populate from the customer record).
  const [territoryID, setTerritoryID] = useState("");
  const [territoryName, setTerritoryName] = useState("");
  const [custID, setCustID] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [salesRepCode, setSalesRepCode] = useState("");
  const [salesRepName, setSalesRepName] = useState("");

  // Default the territory + sales rep to the logged-in user's own values (read
  // from the JWT via sessionStorage) so a rep never has to re-pick themselves.
  // Admin / D99 users see everything and are left to choose explicitly. Runs
  // once on mount; only fills fields that are still blank.
  useEffect(() => {
    if (userScope.isUnrestricted) return;
    const u = getUser();
    if (u.territoryId && !territoryID) {
      setTerritoryID(u.territoryId);
      // Name comes from the login response too, so it shows instantly with no
      // extra lookup on this screen.
      if (u.territoryName) setTerritoryName(u.territoryName);
    }
    if (u.salesRepCode && !salesRepCode) {
      setSalesRepCode(u.salesRepCode);
      if (u.salesRepName) setSalesRepName(u.salesRepName);
      // Warm this rep's customers so the Customer step opens instantly.
      warmRefData(customersDescriptor(u.salesRepCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Option counts → which selection steps to show ───────────────────────
  // Territory / Sales Rep steps only appear when the user has more than one
  // option; with a single option there's nothing to choose, so we auto-select
  // it and skip the step.
  const [territoryCount, setTerritoryCount] = useState(() => optionCount(territoriesDescriptor().key));
  const [repCount, setRepCount] = useState(null);
  // Number of customers scoped to the currently-selected rep (null = not loaded
  // yet, 0 = rep has none → show the "add a customer" prompt on the Customer
  // step). Only meaningful when a rep is picked; customers are rep-scoped.
  const [customerCount, setCustomerCount] = useState(null);

  useEffect(() => {
    const { key } = territoriesDescriptor();
    const read = () => setTerritoryCount(optionCount(key));
    warmRefData(territoriesDescriptor());
    read();
    return subscribeRefData(key, read);
  }, []);

  // Warm the active-system list in the background so the System dropdown in the
  // add-line step opens instantly.
  useEffect(() => { warmRefData(systemsDescriptor()); }, []);

  // The rep / customer counts come from the FRESH answer of each pick (UI-QTE-046 /
  // UI-QTE-048). The cache deliberately does not keep an empty list (a lost Epicor page
  // once read as "no reps", Issue 6 / DEFECT-40), so a count read only from the cache
  // never became 0 and the amber "no sales reps" / "no customers" boxes never showed.
  // An empty answer now shows the box for this pick; the next pick asks again.
  useEffect(() => {
    const d = salesRepsDescriptor(territoryID);
    let cancelled = false;
    setRepCount(optionCount(d.key));
    const read = () => { const n = optionCount(d.key); if (n !== null && !cancelled) setRepCount(n); };
    loadRefData(d.key, d.fetcher)
      .then((list) => { if (!cancelled) setRepCount(list.length); })
      .catch(() => { /* best-effort: the dropdown shows its own load error */ });
    const unsubscribe = subscribeRefData(d.key, read);
    return () => { cancelled = true; unsubscribe(); };
  }, [territoryID]);

  // Track the selected rep's customer count so the Customer step can prompt the
  // user to add one when the rep has no customers. Only the rep-scoped (preload)
  // case is tracked; with no rep picked (D99 admin live-search) we leave it null.
  useEffect(() => {
    if (!salesRepCode) { setCustomerCount(null); return undefined; }
    const d = customersDescriptor(salesRepCode);
    let cancelled = false;
    setCustomerCount(optionCount(d.key));
    const read = () => { const n = optionCount(d.key); if (n !== null && !cancelled) setCustomerCount(n); };
    loadRefData(d.key, d.fetcher)
      .then((list) => { if (!cancelled) setCustomerCount(list.length); })
      .catch(() => { /* best-effort: the dropdown shows its own load error */ });
    const unsubscribe = subscribeRefData(d.key, read);
    return () => { cancelled = true; unsubscribe(); };
  }, [salesRepCode]);

  // Auto-select the only territory / rep (and backfill its description) when
  // there's no real choice — keeps downstream scoping + the review summary
  // populated even though the step is skipped.
  useEffect(() => {
    if (userScope.isUnrestricted) return;
    const list = peekRefData(territoriesDescriptor().key);
    if (!list || list.length !== 1) return;
    const it = list[0];
    const id = it.territoryID || it.TerritoryID || "";
    const nm = it.territoryDesc || it.TerritoryDesc || "";
    if (id && territoryID !== id) setTerritoryID(id);
    if (nm && !territoryName) setTerritoryName(nm);
  }, [territoryCount, territoryID, territoryName, userScope.isUnrestricted]);

  // Backfill the territory description for the current territory (any scope)
  // when it's missing — e.g. the login lookup returned no name. Resolves from
  // the cached list once it lands so the context row shows the description.
  useEffect(() => {
    if (!territoryID || territoryName) return;
    const list = peekRefData(territoriesDescriptor().key);
    if (!list) return;
    const it = list.find((x) => (x.territoryID || x.TerritoryID) === territoryID);
    if (it) setTerritoryName(it.territoryDesc || it.TerritoryDesc || "");
  }, [territoryID, territoryName, territoryCount]);

  useEffect(() => {
    const d = salesRepsDescriptor(territoryID);
    const list = peekRefData(d.key);
    if (!list || !list.length) return;
    const codeOf = (it) => it.salesRepCode || it.SalesRepCode || "";
    if (!salesRepCode) {
      // Prefer the logged-in user's own rep if it's in this territory's list;
      // otherwise auto-pick when there's only one rep to choose from.
      const ownCode = getUser().salesRepCode || "";
      const pick =
        (ownCode && list.find((it) => codeOf(it) === ownCode)) ||
        (list.length === 1 ? list[0] : null);
      if (pick) {
        const code = codeOf(pick);
        const nm = pick.name || pick.Name || "";
        setSalesRepCode(code);
        if (nm) setSalesRepName(nm);
        if (code) warmRefData(customersDescriptor(code));
      }
    } else if (!salesRepName) {
      // Backfill the name beside a code that was defaulted from the JWT.
      const it = list.find((x) => codeOf(x) === salesRepCode);
      if (it) setSalesRepName(it.name || it.Name || "");
    }
  }, [repCount, salesRepCode, salesRepName, territoryID]);

  const [poNum, setPoNum] = useState("");
  const [comments, setComments] = useState("");

  // ── Cart + the line being built in the Add-Line sub-wizard ──────────────
  const [cart, setCart] = useState([]);
  const [draft, setDraft] = useState(() => newDraft());
  const [editingIndex, setEditingIndex] = useState(null);
  // True once the user changes anything while editing an existing line — drives
  // the Plan-details button label (Next when untouched, Save changes when edited).
  const [editDirty, setEditDirty] = useState(false);

  // ── Contacts ────────────────────────────────────────────────────────────
  // Epicor scopes a contact by (CustNum, ShipToNum): customer-level (Sold-To)
  // contacts carry ShipToNum "", ship-to contacts carry their location's number,
  // and the same ConNum is a different person at a different level. So these are
  // two distinct lists — the step used to fetch the Sold-To list alone and offer
  // it as the ship-to picker too, which showed customer contacts in both columns.
  const [custContacts, setCustContacts] = useState([]);
  const [shipToContacts, setShipToContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  // Selections hold the contact rows, not bare ConNums: two ship-to locations
  // can each own a ConNum 1, so a number alone can't identify the pick.
  const [custContact, setCustContact] = useState(null);
  const [shipToContact, setShipToContact] = useState(null);

  // ── Submit state ────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  // Multi-shipment creates take minutes — the overlay can be dismissed to a
  // corner pill while the request keeps running (see the overlay below).
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  // Success modal: shown the moment the quote(s) are created. When exactly one
  // quote was created we also auto-redirect to its view page (cancellable).
  const [successOpen, setSuccessOpen] = useState(false);
  const [autoRedirect, setAutoRedirect] = useState(true);
  // Report style for the quote PDF that is auto-emailed to the recipients on
  // create (system | lines | discount). Rides along in the create body.
  const [reportStyle, setReportStyle] = useState(DEFAULT_REPORT_STYLE);

  // (Commission-tier fetch removed with the rep-discount slider — creation
  //  always quotes at 0% rep discount; discounts live on Edit Quote, action
  //  00079.)

  // ── Draft-line live preview (debounced) ─────────────────────────────────
  const draftDebounce = useRef(null);

  const fetchDraftPreview = useCallback((line) => {
    if (!line || !line.partNum || !salesRepCode || !custID) {
      // DEF-Q06: nothing to price, so nothing is pending - release Add to cart.
      if (line) {
        setDraft((d) => (d && d._key === line._key && d.previewLoading ? { ...d, previewLoading: false } : d));
      }
      return;
    }

    // Reorder-only line (system qty 0) → the system isn't quoted, so there's no
    // system price to preview. Clear any stale preview and skip the call; the
    // reorder parts are estimated client-side and priced by the server on submit.
    if (isReorderOnly(line)) {
      setDraft((d) => (d && d._key === line._key
        ? { ...d, preview: null, previewLoading: false, previewError: null, previewSuggestion: null, ...contractVerdict(null) }
        : d));
      return;
    }

    // Multi-shipment previews have their own endpoint and contract: the system
    // part plus EXACTLY ONE sizing figure (count or ceiling — two rules for one
    // split could disagree, so only the active mode's value travels). The
    // response is the same PricingPreviewResult every other plan returns, plus
    // a shipments[] table because deliveries are not equal.
    if (planCategory(line) === "MSP") {
      const pp = line.paymentPlan || {};
      const sizing =
        line.mspMode === "ceiling"
          ? Number(pp.perShipmentCeiling)
          : Number(pp.shipmentCount);
      if (!sizing || sizing <= 0) {
        setDraft((d) => (d && d._key === line._key
          ? { ...d, preview: null, previewLoading: false, previewError: null, previewSuggestion: null, ...contractVerdict(null) }
          : d));
        return;
      }
      const mspBody = { systemPartNum: line.partNum };
      // The quantity travels too. The box is fixed at 1 for a multi-shipment line
      // (one system, split across the shipments), and the API refuses anything else
      // (D-040 addendum 29) - the UI is the convenience, the API is the rule.
      mspBody.quantity = sendQty(line.quantity);
      if (line.mspMode === "ceiling") mspBody.perShipmentCeiling = sizing;
      else mspBody.shipmentCount = sizing;
      if (salesRepCode) mspBody.salesRepCode = salesRepCode;

      setDraft((d) => (d && d._key === line._key ? { ...d, previewLoading: true, previewError: null } : d));
      fetch(`${API_URL}/api/Msp/preview`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(mspBody),
      })
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          // A rejected plan is a 200 with success:false and an error that
          // already names a workable alternative — surface it verbatim.
          if (!r.ok || data.success === false) {
            throw new Error(data.error || data.message || `HTTP ${r.status}`);
          }
          return data;
        })
        .then((data) => {
          setDraft((d) => (d && d._key === line._key
            ? {
                ...d,
                preview: {
                  ...(data.pricing || {}),
                  commission: data.commission,
                  shipments: data.shipments || [],
                  kitPart: data.partNum,
                  planCode: data.planCode,
                },
                previewLoading: false, previewError: null, previewSuggestion: null,
                // /api/Msp/preview takes no customer, so it resolves no
                // contract — clear any verdict left by the previous plan.
                ...contractVerdict(data),
              }
            : d));
        })
        .catch((err) => {
          setDraft((d) => (d && d._key === line._key
            ? { ...d, previewLoading: false, previewError: err.message, previewSuggestion: null, ...contractVerdict(err) }
            : d));
        });
      return;
    }

    const body = salesPreviewBody(line, salesRepCode, custID);

    setDraft((d) => (d && d._key === line._key ? { ...d, previewLoading: true, previewError: null } : d));

    fetch(`${API_URL}/api/Sales/preview`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data.success === false) {
          const msg = data.error || data.message || `HTTP ${r.status}`;
          // A contract refusal IS a success:false response — the verdict has to
          // survive the throw or the catch below would drop the block.
          throw Object.assign(new Error(msg), {
            suggestion: data.suggestion,
            ctoMinPlan: data.ctoMinimumPlanCode || null,
            ...contractVerdict(data),
          });
        }
        return data;
      })
      .then((data) => {
        const pricing = data.pricing || {};
        // `_key` alone isn't enough: the rep can switch this same line to parts
        // only while the request is in flight. The isReorderOnly branch above
        // already cleared the preview — without this guard the late reply puts
        // the system's price straight back, and the review rollup folds it into
        // Selling Price and Grand Total unconditionally.
        setDraft((d) => (d && d._key === line._key && !isReorderOnly(d)
          ? {
              ...d,
              preview: { ...pricing, commission: data.commission, suggestion: data.suggestion },
              previewLoading: false, previewError: null, previewSuggestion: null,
              // CTO slider floor from this line's net × qty (UI-QTE-084), tagged with the
              // part and quantity it was priced for so a stale floor is never applied.
              ctoMinPlan: data.ctoMinimumPlanCode || null, ctoMinKey: ctoMinKeyOf(line),
              ...contractVerdict(data),
            }
          : d));
      })
      .catch((err) => {
        // UI-QR-N107: drop the last good price too, or the cart row shows an old
        // schedule ("25× $2,291.83") beside the new plan the preview just refused.
        setDraft((d) => (d && d._key === line._key && !isReorderOnly(d)
          ? {
              ...d, preview: null, previewLoading: false, previewError: err.message, previewSuggestion: err.suggestion || null,
              ctoMinPlan: err.ctoMinPlan || null, ctoMinKey: ctoMinKeyOf(line),
              ...contractVerdict(err),
            }
          : d));
      });
  }, [salesRepCode, custID]);

  const debouncedDraftPreview = useCallback((line) => {
    if (draftDebounce.current) clearTimeout(draftDebounce.current);
    draftDebounce.current = setTimeout(() => fetchDraftPreview(line), 300);
  }, [fetchDraftPreview]);

  const updateDraft = useCallback((patch) => {
    setEditDirty(true);
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      // DEF-Q06 (2026-10-31): a price is now on its way (after the debounce) - mark it
      // pending at once so Add to cart cannot commit the old or missing price meanwhile.
      if (next.partNum && !isReorderOnly(next)) next.previewLoading = true;
      debouncedDraftPreview(next);
      return next;
    });
  }, [debouncedDraftPreview]);

  const updateDraftPlan = useCallback((planCode) => {
    setEditDirty(true);
    setDraft((prev) => {
      const cleared = {
        ...prev,
        paymentPlan: { planCode },
        potMode: "months",
        bkMode: "quick",
        ctoMode: "named",
        mspMode: "count",
        tradeIn: planCode === "N30" ? prev.tradeIn : null,
        shippingAmount: planCode === "CTO" ? prev.shippingAmount : null,
        miscChargesAmount: planCode === "CTO" ? prev.miscChargesAmount : null,
        ...(planCode === "POT" ? { paymentPlan: { planCode, numberOfMonths: 12 } } : {}),
        ...(planCode === "BK"  ? { paymentPlan: { planCode, desiredPerInvoice: 2000 } } : {}),
        ...(planCode === "CTO" ? { paymentPlan: { planCode, namedCtoPlan: "C3K" } } : {}),
        // Multi-shipment starts in count mode at 3 deliveries; quantity is
        // forced to 1 because an MSP plan quotes exactly one system.
        ...(planCode === "MSP" ? { paymentPlan: { planCode, shipmentCount: 3 }, quantity: 1 } : {}),
        preview: null, previewError: null, previewLoading: false, previewSuggestion: null,
        ...contractVerdict(null),
      };
      // DEF-Q06: the new plan's price is pending until the preview answers.
      if (cleared.partNum && !isReorderOnly(cleared)) cleared.previewLoading = true;
      debouncedDraftPreview(cleared);
      return cleared;
    });
  }, [debouncedDraftPreview]);

  const startNewDraft = () => { setDraft(newDraft()); setEditingIndex(null); setEditDirty(false); };

  // Commit the draft into the cart (new line or replacing the edited one).
  // UI-QR-N107: a failed create is about the cart as it was; once the rep changes the
  // cart, the old red "Quote creation failed" no longer describes it.
  const clearFailedSubmit = () => setSubmitResult((r) => (r && !r.success ? null : r));

  const commitDraft = (addAnother) => {
    const line = { ...draft };
    clearFailedSubmit();
    setCart((prev) => {
      if (editingIndex != null) {
        const next = [...prev];
        next[editingIndex] = line;
        return next;
      }
      return [...prev, line];
    });
    setEditingIndex(null);
    if (addAnother) startNewDraft();
    else setStep("review");
  };

  const editCartLine = (i) => {
    // Everything is on one screen now, so editing just loads the line back into
    // the draft — the radios & plan details derive from its plan code.
    // The price note belongs to the Review re-check; the draft re-prices itself.
    setDraft({ ...cart[i], priceChangeNote: null });
    setEditingIndex(i);
    setEditDirty(false);
    setStep("addLine");
  };
  const removeCartLine = (i) => { clearFailedSubmit(); setCart((prev) => prev.filter((_, idx) => idx !== i)); };

  // Re-fire the draft preview when rep/customer arrive after the draft opened
  // (reverse-select) or when re-entering edit mode for an existing line.
  useEffect(() => {
    if (step === "addLine" && draft?.partNum && salesRepCode && custID) {
      debouncedDraftPreview(draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, salesRepCode, custID, editingIndex]);

  // ── Review: re-check every cart line against TODAY's price contract ──────
  // UI-QTE-101: a line keeps the contract verdict of its first preview. If the
  // customer's contract changes after that (a Draft gets attached, one is
  // detached), the red banner never appeared and Create Quote stayed open — only
  // the server refused. So on entering Review, and when the customer or rep
  // changes, each line is previewed again with the body the draft used and its
  // contractBlocks / contractNote replaced by the fresh answer. A priced answer
  // also replaces the line's price fields (the same ones the line step sets),
  // so a contract that turned Active re-prices Review and the totals follow;
  // a changed figure gets a priceChangeNote. A refusal keeps the old figures
  // (the line is blocked anyway). Once per entry, not per render: the cart is
  // read through a ref, not a dependency.
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const [contractRecheckPending, setContractRecheckPending] = useState(false);

  useEffect(() => {
    if (step !== "review" || !salesRepCode || !custID) return undefined;
    // Multi-shipment previews take no customer (no contract is resolved there),
    // and a reorder-only line has no system price to preview.
    const lines = cartRef.current.filter((l) => l.partNum && !isReorderOnly(l) && planCategory(l) !== "MSP");
    if (lines.length === 0) return undefined;

    let cancelled = false;
    setContractRecheckPending(true);
    Promise.all(lines.map((line) =>
      fetch(`${API_URL}/api/Sales/preview`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(salesPreviewBody(line, salesRepCode, custID)),
      })
        .then(async (r) => {
          const data = await r.json().catch(() => ({}));
          // A refusal is success:false with the verdict on the body; an answer
          // with no verdict (HTTP error, Epicor blip) leaves the line as it was.
          if (typeof data.contractBlocks !== "boolean") return null;
          const verdict = contractVerdict(data);
          if (verdict.contractBlocks && !verdict.contractNote) {
            verdict.contractNote = data.error || data.message || "Refused by this customer's price contract.";
          }
          // Priced the same way the line step reads a success (fetchDraftPreview).
          if (!r.ok || data.success === false || !data.pricing) return { key: line._key, patch: verdict };
          const preview = { ...data.pricing, commission: data.commission, suggestion: data.suggestion };
          const patch = {
            ...verdict,
            preview,
            previewError: null, previewSuggestion: null,
            ctoMinPlan: data.ctoMinimumPlanCode || null, ctoMinKey: ctoMinKeyOf(line),
          };
          const was = preTaxLinePrice(line.preview);
          const now = preTaxLinePrice(preview);
          if (was != null && now != null && Math.abs(now - was) >= 0.005) {
            patch.priceChangeNote = `Price updated from ${fmtMoney(was)} to ${fmtMoney(now)} after a contract change.`;
          }
          return { key: line._key, patch };
        })
        .catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const byKey = new Map(results.filter(Boolean).map((x) => [x.key, x.patch]));
        if (byKey.size > 0) {
          setCart((prev) => prev.map((l) => (byKey.has(l._key) ? { ...l, ...byKey.get(l._key) } : l)));
        }
      })
      .finally(() => { if (!cancelled) setContractRecheckPending(false); });

    return () => { cancelled = true; setContractRecheckPending(false); };
  }, [step, salesRepCode, custID]);

  // ── Load contacts when entering the Contacts step (two-hop: CustID →
  // CustNum → contacts, because the contacts route has an int constraint). ──
  // One CustCnt call with no shipToNum key returns every level at once (see
  // CustCntController.GetContacts); we split it into the two columns here
  // rather than firing a query per ship-to location.
  useEffect(() => {
    // The Contacts section lives on the combined setup step and reveals as soon
    // as a customer is picked — so load on customer selection, not step entry.
    if (!custID) {
      setCustContacts([]);
      setShipToContacts([]);
      setContactsError(null);
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    setContactsError(null);
    (async () => {
      try {
        const lookup = await fetch(
          `${API_URL}/api/Customer/by-custid/${encodeURIComponent(custID)}`,
          { headers: authHeaders() },
        );
        const lookupData = await lookup.json().catch(() => ({}));
        if (!lookup.ok || lookupData?.success === false) {
          throw new Error(lookupData?.error || lookupData?.message || `Customer lookup: HTTP ${lookup.status}`);
        }
        const custNum = lookupData?.customer?.custNum ?? lookupData?.customer?.CustNum;
        if (!custNum) throw new Error(`No CustNum found for ${custID}.`);
        const r = await fetch(
          `${API_URL}/api/CustCnt?custNum=${encodeURIComponent(custNum)}&inactive=false`,
          { headers: authHeaders() },
        );
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data?.success === false) {
          throw new Error(data?.error || data?.message || `Contacts: HTTP ${r.status}`);
        }
        if (cancelled) return;
        const all = (Array.isArray(data.contacts) ? data.contacts : []).filter(Boolean);
        // ShipToNum "" (or missing) is customer level; anything else — including
        // the literal "0", which is a real ship-to some customers own — is a
        // ship-to contact.
        const isCustLevel = (c) => !String(c.shipToNum ?? c.ShipToNum ?? "").trim();
        setCustContacts(all.filter(isCustLevel));
        setShipToContacts(all.filter((c) => !isCustLevel(c)));
      } catch (err) {
        if (!cancelled) { setCustContacts([]); setShipToContacts([]); setContactsError(err.message); }
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [custID]);

  // A different customer invalidates any contact already picked — ConNums are
  // scoped per customer, so carrying one over would point at a stranger.
  useEffect(() => {
    setCustContact(null);
    setShipToContact(null);
  }, [custID]);

  // ── Reverse-select helpers ──────────────────────────────────────────────

  // Customer → fill SalesRep + Territory from the customer record. The
  // /api/Customer payload carries SalesRepCode/SalesRepName + TerritoryID/
  // TerritoryTerritoryDesc, so a customer-first pick auto-populates the
  // upstream fields (and their descriptions) when they're still blank.
  const handleCustomerSelect = useCallback((item) => {
    const id = item.custID || item.CustID || "";
    setCustID(id);
    setCustomerName(item.name || item.Name || "");

    const repFromCust = item.salesRepCode || item.SalesRepCode || "";
    const repNameFromCust = item.salesRepName || item.SalesRepName || "";
    const terrFromCust = item.territoryID || item.TerritoryID || "";
    const terrNameFromCust =
      item.territoryTerritoryDesc || item.TerritoryTerritoryDesc ||
      item.territoryDesc || item.TerritoryDesc || "";

    if (repFromCust && !salesRepCode) {
      setSalesRepCode(repFromCust);
      if (repNameFromCust) setSalesRepName(repNameFromCust);
    }
    if (terrFromCust && !territoryID) {
      setTerritoryID(terrFromCust);
      if (terrNameFromCust) setTerritoryName(terrNameFromCust);
    } else if (!terrFromCust && repFromCust && !territoryID && repFromCust.includes("-")) {
      setTerritoryID(repFromCust.split("-")[0]);
    }
  }, [salesRepCode, territoryID]);

  // Sales Rep → fill Territory from the rep code prefix.
  const handleSalesRepSelect = useCallback((item) => {
    const code = item.salesRepCode || item.SalesRepCode || "";
    setSalesRepCode(code);
    setSalesRepName(item.name || item.Name || "");
    if (!territoryID && code.includes("-")) {
      setTerritoryID(code.split("-")[0]);
    }
    // Changing rep invalidates a previously-chosen customer that may not
    // belong to this rep.
    if (custID) {
      setCustID("");
      setCustomerName("");
    }
  }, [territoryID, custID]);

  // Territory → clear rep + customer so user re-picks within the new scope.
  // D99 (admin/HQ) is the "see everything" sentinel — every rep is still in
  // scope, so we keep whatever the user already picked.
  const handleTerritorySelect = useCallback((item) => {
    const id = item.territoryID || item.TerritoryID || "";
    setTerritoryID(id);
    setTerritoryName(item.territoryDesc || item.TerritoryDesc || "");
    if (isAdminTerritory(id)) return;
    if (salesRepCode && !salesRepCode.startsWith(`${id}-`) && salesRepCode !== id) {
      setSalesRepCode("");
      setSalesRepName("");
      // Clearing the rep invalidates the customer (customer is rep-scoped).
      if (custID) {
        setCustID("");
        setCustomerName("");
      }
    }
  }, [salesRepCode, custID]);

  // ── Memoised configs (re-built when narrowing values change) ────────────
  const salesRepConfig = useMemo(() => buildSalesRepConfig(territoryID), [territoryID]);
  const customerConfig = useMemo(
    () => buildCustomerConfig(salesRepCode),
    [salesRepCode]
  );

  // ── Quote grouping + totals (computed over the cart) ────────────────────
  const quoteCount = useMemo(() => {
    const ready = cart.filter((l) => l.partNum);
    if (ready.length === 0) return 0;
    const hasN30 = ready.some((l) => planCategory(l) === "N30");
    let count = hasN30 ? 1 : 0;
    ready.filter((l) => planCategory(l) !== "N30").forEach((l) => {
      // A multi-shipment line in separate-quote mode builds one Epicor quote
      // per shipment; every other non-N30 line is one quote.
      if (planCategory(l) === "MSP" && l.mspSeparateQuotes) {
        count +=
          Number(l.preview?.numberOfInvoices) ||
          Number(l.paymentPlan?.shipmentCount) || 1;
      } else {
        count += 1;
      }
    });
    return count;
  }, [cart]);

  const rollup = useMemo(() => {
    const acc = {
      sellingPrice: 0, repDiscountAmount: 0, systemDiscount: 0,
      tariffAmount: 0, setupFee: 0, interestAmount: 0,
      totalInvoiceFees: 0, taxAmount: 0, reorderTotal: 0, totalPrice: 0,
      tradeInCredit: 0, customerExtras: 0,
    };
    let priced = 0;
    cart.forEach((l) => {
      // Reorder parts are quoted as extra N30 lines, so they belong in the
      // grand total — same as the cart total (otherwise the review understates).
      acc.reorderTotal += reorderEstimate(l);
      const p = l.preview;
      if (!p) return;
      priced += 1;
      acc.sellingPrice      += Number(p.sellingPrice      || 0);
      acc.repDiscountAmount += Number(p.repDiscountAmount || 0);
      acc.systemDiscount    += Number(p.systemDiscount    || 0);
      acc.tariffAmount      += Number(p.tariffAmount      || 0);
      acc.setupFee          += Number(p.setupFee          || 0);
      acc.interestAmount    += Number(p.interestAmount    || 0);
      acc.totalInvoiceFees  += Number(p.totalInvoiceFees  || 0);
      acc.taxAmount         += Number(p.taxAmount         || 0);
      acc.tradeInCredit     += Number(p.tradeInValue      || 0);   // Issue 25: shown as its own row
      acc.customerExtras    += Number(p.customerExtras    || 0);   // Issue 37: CTO shipping + misc, already inside totalPrice
      // Pre-tax: Epicor quote totals (QuoteAmt) exclude tax — tax is applied when
      // the quote becomes an order. Subtract the estimate so the grand total
      // matches the created quote. The estimate is surfaced separately.
      acc.totalPrice        += Number(p.totalPrice || 0) - Number(p.taxAmount || 0);
    });
    acc.totalPrice += acc.reorderTotal;
    return { ...acc, pricedLines: priced, totalLines: cart.length };
  }, [cart]);

  // Whether this submit includes a multi-shipment plan — drives the patient
  // "submitted, the email will arrive" overlay instead of the quick blocking one.
  const cartHasMsp = useMemo(
    () => cart.some((l) => planCategory(l) === "MSP" && !isReorderOnly(l)),
    [cart]
  );

  const cartTotal = useMemo(
    // Pre-tax (matches the Epicor quote total; tax is applied at order).
    () => cart.reduce((sum, l) => sum + (Number(l.preview?.totalPrice) || 0) - (Number(l.preview?.taxAmount) || 0) + reorderEstimate(l), 0),
    [cart]
  );

  // ── Step gating ─────────────────────────────────────────────────────────
  // Contacts are optional metadata (they ride in the comments, not a required
  // API field). Only insist on a selection when the customer actually has
  // contacts to choose from — otherwise an empty contact list would dead-end
  // a quote that the old flow could create. Each column is gated on its own
  // list: a customer with no ship-to contacts on file must still get through.
  const contactsChosen =
    (custContacts.length === 0 || custContact != null) &&
    (shipToContacts.length === 0 || shipToContact != null);
  // A contract-blocked line is refused by the submit for the reason the preview
  // already gave, so the Create button stays shut until it is changed or removed.
  const contractBlockedLines = useMemo(() => cart.filter(isContractBlocked), [cart]);
  // Also shut while the Review step's contract re-check (UI-QTE-101) is out —
  // it is the answer that may close it for good.
  const canSubmit =
    cart.length > 0 && contactsChosen && contractBlockedLines.length === 0 &&
    !contractRecheckPending && !submitting && submitResult?.success !== true;

  // ── Reset the wizard back to a clean first step ("Create another"). ──────
  // Without this, the previous cart stays in state — if the user clicks
  // Create Quote again they'd submit duplicate Epicor quotes.
  const resetWizard = () => {
    setTerritoryID("");
    setTerritoryName("");
    setSalesRepCode("");
    setSalesRepName("");
    setCustID("");
    setCustomerName("");
    setPoNum("");
    setComments("");
    setCart([]);
    setDraft(newDraft());
    setEditingIndex(null);
    setCustContacts([]);
    setShipToContacts([]);
    setContactsError(null);
    setCustContact(null);
    setShipToContact(null);
    setSubmitResult(null);
    setTerritorySkipped(false);
    setStep("setup");
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitResult(null);
    setOverlayDismissed(false);

    // The /api/Sales/quote contract has no contact fields, so the chosen
    // contacts ride along in the header comments (the approach the simulator
    // documents).
    // The ship-to ConNum is only meaningful alongside its location (ConNum is
    // scoped per ship-to), so the note carries both.
    const contactNote = [
      custContact ? `customerContact=${custContact.conNum}` : null,
      shipToContact ? `shipToContact=${shipToContact.conNum}` : null,
      shipToContact ? `shipTo=${shipToContact.shipToNum}` : null,
    ].filter(Boolean).join(", ");
    const mergedComments = [comments, contactNote].filter(Boolean).join(" · ") || null;

    // Multi-shipment lines do not ride the /api/Sales/quote cart — each one is
    // its own plan with its own create endpoint (and possibly its own fan-out
    // into several Epicor quotes). Partition the cart and merge the results.
    const mspLines = cart.filter((l) => planCategory(l) === "MSP" && !isReorderOnly(l));
    const otherLines = cart.filter((l) => !(planCategory(l) === "MSP" && !isReorderOnly(l)));

    const mergedQuotes = [];
    const notices = [];
    const errors = [];

    for (const l of mspLines) {
      const pp = l.paymentPlan || {};
      const u = getUser();
      const mspBody = {
        systemPartNum: l.partNum,
        quantity: sendQty(l.quantity),   // 1 on every multi-shipment line; the API refuses more
        custID,
        salesRepCode,
        shipToNum: shipToContact ? String(shipToContact.shipToNum) : null,
        poNum: poNum || null,
        quoteComment: mergedComments,
        submittedBy: u.name || u.email || "web",
        submitterEmail: u.email || null,
        separateQuotePerShipment: !!l.mspSeparateQuotes,
        separateDocumentPerShipment: !!l.mspSeparateDocs,
      };
      if (l.mspMode === "ceiling") mspBody.perShipmentCeiling = Number(pp.perShipmentCeiling);
      else mspBody.shipmentCount = Number(pp.shipmentCount);

      try {
        const r = await fetch(`${API_URL}/api/Msp/quote`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(mspBody),
        });
        const data = await r.json().catch(() => ({}));

        if (data.mode === "separate-quote-per-shipment") {
          // One quote per shipment: the good ones are kept even when a later
          // one fails, so read created-vs-expected and surface failures.
          const perQuote = Array.isArray(data.quotes) ? data.quotes : [];
          (data.quoteNums || []).forEach((qn) => {
            const q = perQuote.find((x) => (x.quoteNum ?? x.QuoteNum) === qn);
            mergedQuotes.push({
              quoteNum: qn,
              termsCode: data.termsCode || null,
              totalPrice: q?.customerSees,
            });
          });
          if ((data.quotesCreated ?? 0) < (data.quotesExpected ?? 0)) {
            errors.push(
              `${l.partNum}: only ${data.quotesCreated} of ${data.quotesExpected} shipment quotes were built.`
            );
          }
          (data.failures || []).forEach((f) => errors.push(String(f)));
          if (data.error) errors.push(String(data.error));
        } else if (!r.ok || data.success === false || !data.quoteNum) {
          errors.push(`${l.partNum}: ${data.error || data.message || `HTTP ${r.status}`}`);
          if (data.needsManualCleanup) {
            errors.push(`${l.partNum}: an empty quote was left in Epicor and must be deleted by hand.`);
          }
        } else {
          mergedQuotes.push({
            quoteNum: data.quoteNum,
            termsCode: data.termsCode || null,
            // The quote total is the preview's pre-tax figure (lines + charges);
            // the create response carries tax/commission but not the total.
            totalPrice:
              l.preview?.totalPrice != null
                ? Number(l.preview.totalPrice) - Number(l.preview.taxAmount || 0)
                : undefined,
          });
          (data.warnings || []).forEach((w) => notices.push(String(w)));
          if (data.needsManualCleanup) {
            errors.push(`Quote #${data.quoteNum}: needs manual cleanup — check it in Epicor.`);
          }
        }
      } catch (err) {
        errors.push(`${l.partNum}: ${err.message}`);
      }
    }

    if (otherLines.length === 0) {
      const anyCreated = mergedQuotes.length > 0;
      setSubmitResult({
        success: anyCreated,
        quotes: mergedQuotes,
        notices,
        error: errors.length ? errors.join(" · ") : null,
      });
      if (anyCreated) {
        setAutoRedirect(true);
        setSuccessOpen(true);
      }
      setSubmitting(false);
      return;
    }

    const body = {
      custID,
      salesRepCode,
      poNum: poNum || null,
      comments: mergedComments,
      reportStyle,
      lines: otherLines.flatMap((l) => {
        // Reorder parts ride along as extra Net-30 lines (grouped under the
        // system in the UI, separate lines on the wire).
        const reorderLines = selectedReorderBoms(l).map((b) => ({
          partNum: b.partNum,
          quantity: sendQty(b.quantity),
          paymentPlan: { planCode: "N30" },
          repDiscountPercent: 0,
        }));
        // Reorder-only line (system qty 0) → don't quote the system itself;
        // send just the reorder parts.
        if (isReorderOnly(l)) return reorderLines;

        const planCode = effectivePlanCode(l);
        const paymentPlan = { ...buildPaymentPlan(l), planCode };
        const out = {
          partNum: l.partNum,
          quantity: sendQty(l.quantity),
          paymentPlan,
          repDiscountPercent: Number(l.repDiscountPercent) || 0,
        };
        if (planCategory(l) === "N30" && l.tradeIn && l.tradeIn.partNum) out.tradeIn = l.tradeIn;
        if (planCategory(l) === "CTO" && l.shippingAmount)        out.shippingAmount = Number(l.shippingAmount);
        if (planCategory(l) === "CTO" && l.miscChargesAmount)     out.miscChargesAmount = Number(l.miscChargesAmount);
        return [out, ...reorderLines];
      }),
    };

    try {
      const r = await fetch(`${API_URL}/api/Sales/quote`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        errors.push(data.error || data.message || `HTTP ${r.status}`);
      } else {
        mergedQuotes.push(...(data.quotes || []));
      }
    } catch (err) {
      errors.push(err.message);
    }

    const anyCreated = mergedQuotes.length > 0;
    setSubmitResult({
      success: anyCreated,
      quotes: mergedQuotes,
      notices,
      error: errors.length ? errors.join(" · ") : null,
    });
    if (anyCreated) {
      // Loading overlay drops; success modal (with details + redirect) opens.
      setAutoRedirect(true);
      setSuccessOpen(true);
    }
    setSubmitting(false);
  };

  // Quotes created on this submit + the lone quote number (when exactly one),
  // which drives the auto-redirect target.
  const createdQuotes = submitResult?.success ? (submitResult.quotes || []) : [];
  const soleQuoteNum =
    createdQuotes.length === 1
      ? (createdQuotes[0].quoteNum ?? createdQuotes[0].QuoteNum ?? null)
      : null;

  // Auto-redirect to the quote view page ~2.5s after a single-quote create,
  // unless the rep cancels (clicks "Stay here") or closes the modal.
  useEffect(() => {
    if (!successOpen || !autoRedirect || soleQuoteNum == null) return;
    const t = setTimeout(() => navigate(`/quote/edit/${soleQuoteNum}`), 2500);
    return () => clearTimeout(t);
  }, [successOpen, autoRedirect, soleQuoteNum, navigate]);

  // ── Render ──────────────────────────────────────────────────────────────
  // Top padding = 78px to clear the fixed floating navbar (sits at top:14px
  // with height ≈ 36–48px; 78px gives clean separation on both desktop and
  // mobile where the collapse can grow taller).
  return (
    <div className="qw-root content-halo dyn-form-shell dyn-form-2x" style={{ padding: "28px 24px 36px", borderRadius: 16 }}>
      <div className="dyn-form-scroll">
      {/* Full-page busy overlay while the quote is being created in Epicor.
          Ordinary quotes finish in seconds, so they block + reassure. A
          multi-shipment quote writes one kit line per shipment PLUS every
          component (a 3-shipment S9MAXI is 42 Epicor lines), which takes
          minutes — so its overlay says "submitted", explains the email will
          arrive on its own, and offers to get out of the way. The create keeps
          running server-side either way; dismissing only hides the overlay. */}
      {submitting && !overlayDismissed && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 4000,
          background: "rgba(15,23,42,0.45)", backdropFilter: "blur(1px)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "28px 40px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
            boxShadow: "0 20px 50px rgba(0,0,0,.28)", maxWidth: 400, textAlign: "center",
          }}>
            <Spinner animation="border" style={{ color: "#4F46E5", width: 42, height: 42 }} />
            {cartHasMsp ? (
              <>
                <div style={{ fontWeight: 700, color: "#0F172A", fontSize: 15 }}>Quote submitted — Epicor is building it</div>
                <div style={{ fontSize: 12.5, color: "#64748B", lineHeight: 1.5 }}>
                  A multi-shipment quote writes every shipment and component to Epicor,
                  so this usually takes <strong>a few minutes</strong>. The quote email is sent to the
                  rep, distributor and customer contact automatically the moment it&apos;s ready —
                  you don&apos;t have to wait here.
                </div>
                <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={() => setOverlayDismissed(true)}>
                  Continue in background
                </button>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, color: "#0F172A", fontSize: 15 }}>Creating your quote…</div>
                <div style={{ fontSize: 12.5, color: "#64748B", lineHeight: 1.4 }}>
                  This can take a few moments — please don’t close or leave this window.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Minimized progress pill once the overlay is dismissed — the result
          modal still pops the moment Epicor answers. */}
      {submitting && overlayDismissed && (
        <div style={{
          position: "fixed", right: 18, bottom: 18, zIndex: 4000,
          background: "#0F172A", color: "#fff", borderRadius: 999,
          padding: "10px 18px", display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,.35)", fontSize: 13,
        }}>
          <Spinner animation="border" size="sm" />
          Building the quote in Epicor… the confirmation will pop up here when it&apos;s done.
        </div>
      )}

      {/* Success modal — appears the instant the quote(s) are created. Shows the
          new quote details and (for a single quote) auto-redirects to its view
          page after a short beat; the "View quote" button does the same now. */}
      {successOpen && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 4100,
            background: "rgba(15,23,42,0.5)", backdropFilter: "blur(2px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
          onClick={() => { setAutoRedirect(false); setSuccessOpen(false); }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 18, padding: "26px 28px",
              width: "100%", maxWidth: 440, boxShadow: "0 24px 60px rgba(0,0,0,.32)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              aria-label="Close"
              onClick={() => { setAutoRedirect(false); setSuccessOpen(false); }}
              style={{
                position: "absolute", top: 12, right: 14, border: "none", background: "none",
                fontSize: 22, lineHeight: 1, color: "#94A3B8", cursor: "pointer",
              }}
            >
              ×
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 999, background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
                <FaCheckCircle size={20} />
              </div>
              <div>
                <div style={{ fontWeight: 800, color: "#065F46", fontSize: 17 }}>
                  Quote{createdQuotes.length === 1 ? "" : "s"} created
                </div>
                <div style={{ fontSize: 12.5, color: "#64748B" }}>
                  Saved to Epicor{customerName ? ` · ${customerName}` : ""}
                </div>
              </div>
            </div>

            {createdQuotes.length > 0 ? (
              <div className="qw-stack" style={{ marginBottom: 14 }}>
                {createdQuotes.map((q, i) => {
                  const qn = q.quoteNum ?? q.QuoteNum;
                  const total = q.totalPrice ?? q.pricing?.totalPrice ?? q.pricing?.TotalPrice;
                  const terms = q.termsCode || q.TermsCode || q.planCode || q.PlanCode;
                  return (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 12px", background: "#F0FDF4", border: "1px solid #A7F3D0", borderRadius: 10, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700, color: "#0F172A" }}>Quote #{qn}</div>
                        <div className="qw-mono" style={{ fontSize: 12, color: "#64748B" }}>
                          {terms ? `terms ${terms}` : ""}{q.lineCount ? `${terms ? " · " : ""}${q.lineCount} line${q.lineCount === 1 ? "" : "s"}` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {total != null && <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A" }}>{fmtMoney(total)}</span>}
                        {qn != null && (
                          <button className="qw-btn qw-btn-primary qw-btn-sm" onClick={() => navigate(`/quote/edit/${qn}`)}>
                            View quote <FaChevronRight />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#065F46", marginBottom: 14 }}>
                The quote was created in Epicor.
              </div>
            )}

            {(submitResult?.notices || []).length > 0 && (
              <div className="qw-info qw-info-amber" style={{ marginBottom: 12, fontSize: 12.5 }}>
                <strong>Warnings</strong>
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {submitResult.notices.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}
            {submitResult?.error && (
              <div className="qw-info" style={{ marginBottom: 12, fontSize: 12.5, background: "#FEF2F2", color: "#991B1B" }}>
                <strong>Not everything was created:</strong> {submitResult.error}
              </div>
            )}

            {soleQuoteNum != null && autoRedirect && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#64748B", marginBottom: 12 }}>
                <Spinner animation="border" size="sm" style={{ color: "#4F46E5" }} />
                Taking you to the quote view…
                <button
                  className="qw-btn qw-btn-ghost qw-btn-sm"
                  style={{ marginLeft: "auto" }}
                  onClick={() => setAutoRedirect(false)}
                >
                  Stay here
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {soleQuoteNum != null && (
                <button className="qw-btn qw-btn-primary" onClick={() => navigate(`/quote/edit/${soleQuoteNum}`)}>
                  View quote <FaChevronRight />
                </button>
              )}
              <button className="qw-btn qw-btn-outline" onClick={() => { setAutoRedirect(false); setSuccessOpen(false); }}>
                Stay on this page
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 11l3 3l8-8" />
              <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
            </svg>
          </div>
          <div>
            <div className="qw-header-title">Create Sales Quote</div>
            <div className="qw-header-sub">Shukla Medicals · guided quote builder</div>
          </div>
        </div>
        <button
          className="qw-btn qw-btn-sm"
          style={{ background: "rgba(255,255,255,.18)", color: "#fff" }}
          onClick={() => navigate("/quote")}
        >
          <FaChevronLeft /> Back to Quotes
        </button>
      </div>

      <div className="qw-card" style={{ marginBottom: 16 }}>
        <StepDots step={step} />
      </div>

      <div className="qw-card qw-pad qw-fade" key={step} style={{ minHeight: 420 }}>
          {step === "setup" && (
            <SetupStep
              userScope={userScope}
              territoryConfig={AUTOCOMPLETE_TERRITORY}
              salesRepConfig={salesRepConfig}
              customerConfig={customerConfig}
              territoryID={territoryID}
              setTerritoryID={setTerritoryID}
              territoryName={territoryName}
              onTerritorySelect={handleTerritorySelect}
              territorySkipped={territorySkipped}
              onSkipTerritory={() => setTerritorySkipped(true)}
              salesRepCode={salesRepCode}
              setSalesRepCode={setSalesRepCode}
              salesRepName={salesRepName}
              repCount={repCount}
              onSalesRepSelect={handleSalesRepSelect}
              custID={custID}
              setCustID={setCustID}
              customerName={customerName}
              customerCount={customerCount}
              onCustomerSelect={handleCustomerSelect}
              custContacts={custContacts}
              shipToContacts={shipToContacts}
              contactsLoading={contactsLoading}
              contactsError={contactsError}
              custContact={custContact}
              setCustContact={setCustContact}
              shipToContact={shipToContact}
              setShipToContact={setShipToContact}
              canAddLines={contactsChosen && !contactsLoading}
              onAddLines={() => { if (editingIndex == null) startNewDraft(); setStep("addLine"); }}
            />
          )}
          {step === "addLine" && (
            <AddLineStep
              draft={draft}
              editingIndex={editingIndex}
              editDirty={editDirty}
              cartLength={cart.length}
              territoryID={territoryID}
              territoryName={territoryName}
              salesRepCode={salesRepCode}
              salesRepName={salesRepName}
              custID={custID}
              customerName={customerName}
              custContact={custContact}
              shipToContact={shipToContact}
              onUpdate={updateDraft}
              onUpdatePlan={updateDraftPlan}
              onCommit={commitDraft}
              onBackToCart={() => setStep(cart.length ? "review" : "setup")}
            />
          )}
          {step === "review" && (
            <ReviewStep
              territoryID={territoryID}
              territoryName={territoryName}
              salesRepCode={salesRepCode}
              salesRepName={salesRepName}
              custID={custID}
              customerName={customerName}
              poNum={poNum}
              setPoNum={setPoNum}
              comments={comments}
              setComments={setComments}
              reportStyle={reportStyle}
              setReportStyle={setReportStyle}
              custContact={custContact}
              shipToContact={shipToContact}
              cart={cart}
              cartTotal={cartTotal}
              quoteCount={quoteCount}
              rollup={rollup}
              canSubmit={canSubmit}
              submitting={submitting}
              submitResult={submitResult}
              onAddLine={() => { startNewDraft(); setStep("addLine"); }}
              onEdit={editCartLine}
              onRemove={removeCartLine}
              // Back walks one step: Review → Add Line (setup is another Back away).
              onBack={() => { startNewDraft(); setStep("addLine"); }}
              onSubmit={handleSubmit}
              onCreateAnother={resetWizard}
              onViewQuote={(qn) => navigate(`/quote/edit/${qn}`)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// StepDots — simulator-style dot progress bar (labels hidden on mobile).
// =============================================================================
function StepDots({ step }) {
  const idx = WIZARD_STEPS.findIndex((s) => s.id === step);
  const active = idx < 0 ? WIZARD_STEPS.length : idx;
  return (
    <div className="qw-progress">
      {WIZARD_STEPS.map((s, i) => {
        const state = i < active ? "complete" : i === active ? "active" : "";
        const labelColor = i === active ? "#4F46E5" : (i < active ? "#10B981" : "#94A3B8");
        return (
          <React.Fragment key={s.id}>
            <div className={`qw-step-dot ${state}`}>
              {i < active ? <FaCheckCircle size={13} /> : i + 1}
            </div>
            <div className="qw-step-label d-none d-md-block" style={{ color: labelColor }}>
              {s.label}
            </div>
            {i < WIZARD_STEPS.length - 1 && (
              <div className={`qw-step-line ${i < active ? "complete" : ""}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// =============================================================================
// SetupStep — Territory → Sales Rep → Customer → Contacts combined into one
// progressive step. Only the Territory field shows at first; each selection
// reveals the next section below it. Reverse-select still works (picking a
// customer auto-fills the rep & territory upstream). Ends with the
// "Add line items" button that jumps to the Add-Line step.
// =============================================================================
function SetupStep({
  userScope, territoryConfig, salesRepConfig, customerConfig,
  territoryID, setTerritoryID, territoryName, onTerritorySelect,
  territorySkipped, onSkipTerritory,
  salesRepCode, setSalesRepCode, salesRepName, repCount, onSalesRepSelect,
  custID, setCustID, customerName, customerCount, onCustomerSelect,
  custContacts, shipToContacts, contactsLoading, contactsError,
  custContact, setCustContact, shipToContact, setShipToContact,
  canAddLines, onAddLines,
}) {
  const showRep = Boolean(territoryID) || territorySkipped;
  const showCustomer = showRep && Boolean(salesRepCode);
  const showContacts = showCustomer && Boolean(custID);
  // Empty-list prompts (same rules the old dedicated steps used) — only once
  // the count has loaded (0, not null) and nothing is picked yet.
  const showNoReps = Boolean(territoryID) && repCount === 0 && !salesRepCode;
  const showNoCustomers = Boolean(salesRepCode) && customerCount === 0 && !custID;

  return (
    <div>
      <h2 className="qw-h2">Set up your quote</h2>
      <p className="qw-sub" style={{ marginBottom: 22, fontSize: 11, color: "#94A3B8" }}>
        Each selection reveals the next — the territory scopes the reps, the rep scopes the customers.
      </p>

      {/* ── Territory ──────────────────────────────────────────────────── */}
      <div className="qw-field">
        <SearchableSelect
          config={territoryConfig}
          value={territoryID}
          onChange={setTerritoryID}
          onSelectItem={onTerritorySelect}
          placeholder="Type ID or description…"
          variant="card"
          label={`Territory${!userScope.isUnrestricted ? " *" : ""}`}
          secondaryText={territoryName}
        />
        {!userScope.isUnrestricted && userScope.territoryId && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#94A3B8", lineHeight: 1.4 }}>
            Showing options scoped to your territory {userScope.territoryId} and its sub-territories.
          </div>
        )}
        {userScope.isUnrestricted && !territoryID && !territorySkipped && (
          <div style={{ marginTop: 8 }}>
            <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={onSkipTerritory}>
              Skip — search reps across all territories <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* ── Sales rep — revealed once a territory is picked (or skipped) ── */}
      {showRep && (
        <div className="qw-field qw-fade" style={{ marginTop: 18 }}>
          <SearchableSelect
            config={salesRepConfig}
            value={salesRepCode}
            onChange={setSalesRepCode}
            onSelectItem={onSalesRepSelect}
            placeholder={territoryID && !isAdminTerritory(territoryID) ? `Reps in ${territoryID}…` : "Type code or name…"}
            variant="card"
            label="Sales Rep *"
            secondaryText={salesRepName}
          />
          {territoryID && !isAdminTerritory(territoryID) && (
            <div style={{ marginTop: 6, fontSize: 11, color: "#94A3B8", lineHeight: 1.4 }}>
              Showing options scoped to territory {territoryID} and its sub-territories.
            </div>
          )}
          {showNoReps && (
            <div className="qw-info qw-info-amber" style={{ marginTop: 14, fontSize: 13, flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
              <div>
                Territory <strong>{territoryID}</strong> has no sales reps assigned. Add a sales rep to this
                territory before creating a quote for it.
              </div>
              <a
                href="/sales-representatives/create"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 700, color: "#92400E", textDecoration: "underline" }}
              >
                Add a sales rep →
              </a>
            </div>
          )}
        </div>
      )}

      {/* ── Customer — revealed once a rep is picked ───────────────────── */}
      {showCustomer && (
        <div className="qw-field qw-fade" style={{ marginTop: 18 }}>
          <SearchableSelect
            config={customerConfig}
            value={custID}
            onChange={setCustID}
            onSelectItem={onCustomerSelect}
            placeholder={salesRepCode ? `Customers of ${salesRepCode}…` : "Type ID or name…"}
            variant="card"
            label="Customer *"
            secondaryText={customerName}
          />
          <div style={{ marginTop: 6, fontSize: 11, color: "#94A3B8", lineHeight: 1.4 }}>
            Only customers assigned to this rep are shown. Picking one auto-fills the rep &amp; territory.
          </div>
          {showNoCustomers && (
            <div className="qw-info qw-info-amber" style={{ marginTop: 14, fontSize: 13, flexDirection: "column", alignItems: "flex-start", gap: 6 }}>
              <div>
                Sales rep <strong>{salesRepCode}</strong> has no customers available. Add a customer and
                assign them to this rep before creating a quote.
              </div>
              <a
                href="/customer/create"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 700, color: "#92400E", textDecoration: "underline" }}
              >
                Add a customer →
              </a>
            </div>
          )}
        </div>
      )}

      {/* ── Contacts — revealed once a customer is picked ──────────────── */}
      {showContacts && (
        <div className="qw-fade" style={{ marginTop: 24 }}>
          <div style={{ height: 1, background: "#E2E8F0", margin: "0 0 18px" }} />
          <h2 className="qw-h2" style={{ fontSize: 17 }}>Choose contacts</h2>
          <p className="qw-sub" style={{ marginBottom: 16 }}>
            Customer contact is the primary signer; ship-to contact receives the order.{customerName ? ` · ${customerName}` : ""}
          </p>
          <ContactsPicker
            custContacts={custContacts}
            shipToContacts={shipToContacts}
            loading={contactsLoading}
            error={contactsError}
            custContact={custContact}
            setCustContact={setCustContact}
            shipToContact={shipToContact}
            setShipToContact={setShipToContact}
          />
          <div className="qw-actions">
            <span />
            <button
              className="qw-btn qw-btn-primary"
              disabled={!canAddLines}
              onClick={onAddLines}
            >
              Add line items <FaChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Add-Line step — everything on one screen. Product pick reveals the
// buying-option radios; the radios resolve to one of the four Epicor plans
// (N30 / BK / POT / CTO), whose details + calculations render below:
//   Pay now  + single invoice → N30      Pay now  + multi invoice → BK
//   Pay Over Time + non-returnable → POT Pay Over Time + returnable → CTO
// =============================================================================
function planDetailsReady(d) {
  const cat = planCategory(d);
  if (cat === "N30") return true;
  if (cat === "POT") return !!(d.paymentPlan.numberOfMonths || d.paymentPlan.desiredPerInvoice);
  // Gap 17 (2026-10-31): a typed -500 is truthy but not a split amount - the API refuses it.
  if (cat === "BK")  return Number(d.paymentPlan.desiredPerInvoice) > 0;
  if (cat === "CTO") return d.ctoMode === "custom" ? !!d.paymentPlan.customMonthlyAmount : !!d.paymentPlan.namedCtoPlan;
  if (cat === "MSP") {
    return d.mspMode === "ceiling"
      ? Number(d.paymentPlan.perShipmentCeiling) > 0
      : Number(d.paymentPlan.shipmentCount) >= MSP_MIN_SHIPMENTS;
  }
  return false;
}

// (catPillClass removed — the plan-code pills it colored are hidden for now.)

// One radio row — label on top, option CARDS below (.qw-radio-card in
// QuoteWizard.css). The native radio stays in the DOM, visually hidden.
//
// `name` is REQUIRED and must be unique on the page. Without it the inputs are
// four unrelated radios that merely look like a group: no arrow-key roving, and
// every option its own tab stop. `role="radiogroup"` is what tells a screen
// reader the label above belongs to the options below.
function RadioRow({ name, label, hint, options, value, onChange, disabledValues = [] }) {
  return (
    <div>
      <label className="qw-label" style={{ marginBottom: 6 }}>{label}</label>
      <div className="qw-radio-group" role="radiogroup" aria-label={label}>
        {options.map((o) => {
          const disabled = disabledValues.includes(o.value);
          const selected = value === o.value;
          return (
            <label
              key={o.value}
              className={`qw-radio-card${selected ? " selected" : ""}${disabled ? " disabled" : ""}`}
            >
              <input
                type="radio"
                name={name}
                checked={selected}
                disabled={disabled}
                onChange={() => !disabled && onChange(o.value)}
              />
              <span className="qw-radio-dot" aria-hidden="true" />
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
      {hint && <div style={{ marginTop: 6, fontSize: 11.5, color: "#94A3B8", lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}

function AddLineStep({ draft, editingIndex, editDirty, cartLength, territoryID, territoryName, salesRepCode, salesRepName, custID, customerName, custContact, shipToContact, onUpdate, onUpdatePlan, onCommit, onBackToCart }) {
  const d = draft;
  const lineNo = editingIndex != null ? editingIndex + 1 : cartLength + 1;

  // Radio state derives from the resolved plan code — no separate state to
  // drift out of sync (an edited BK line reopens as Pay now + multi invoices,
  // an MSP line as Pay now + multi shipment).
  const cat = planCategory(d);
  const payMode = cat === "POT" || cat === "CTO" ? "pot" : "now";
  const invoiceMode = cat === "BK" || cat === "MSP" ? "multi" : "single";
  const returnable = cat === "CTO";
  const shipmentMode = cat === "MSP" ? "multi" : "single";
  // Reorder parts (and reorder-only lines) lock the group to Net 30 —
  // Pay now + single invoice; the other radio options are disabled.
  const locked = !!d.reorder || isReorderOnly(d);

  const reorderOnlyIncomplete = isReorderOnly(d) && selectedReorderBoms(d).length === 0;
  // DEF-Q06 (2026-10-31): never commit a line while its price is still loading.
  const pricingPending = !!d.previewLoading;
  const commitDisabled = !d.partNum || !planDetailsReady(d) || reorderOnlyIncomplete || isContractBlocked(d) || isUnpriced(d) || isOverTermCap(d) || hasNegativeQuantity(d) || hasFractionalQuantity(d) || !!planAmountProblem(d) || pricingPending;
  const commitHint = pricingPending ? "Pricing is still loading - wait for the price before adding this line." : undefined;

  /* T-002: the three context items sat in a 3-up flex row whose label and code
     were both `white-space: nowrap`, so they could not shrink into their
     `flex: 1 1 0` track and overflowed the card on phones. Layout now lives in
     .qw-ctx / .qw-ctx-item (QuoteWizard.css): 3-up on desktop exactly as before,
     a label/value grid stacked one-per-row under 992px. */
  const headerItem = (label, code, name) => (
    <div className="qw-ctx-item">
      <span className="qw-ctx-label">{label}</span>
      <strong className="qw-ctx-code">{code || "—"}</strong>
      {name && <span className="qw-ctx-name" title={name}>{name}</span>}
    </div>
  );

  return (
    <div>
      <div>
        <div className="qw-eyebrow">{editingIndex != null ? `Editing line ${lineNo}` : `Line item ${lineNo}`}</div>
      </div>
      <div className="qw-ctx">
        {headerItem("Territory", territoryID, territoryName)}
        {headerItem("Sales Rep", salesRepCode, salesRepName)}
        {headerItem("Customer", custID, customerName)}
        {headerItem(
          "Customer Contact",
          custContact ? (custContact.name || `Contact ${custContact.conNum}`) : "",
          custContact?.eMailAddress
        )}
        {headerItem(
          "Ship-To Contact",
          shipToContact ? (shipToContact.name || `Contact ${shipToContact.conNum}`) : "",
          shipToContact ? `ShipTo ${shipToContact.shipToNum}${shipToContact.eMailAddress ? ` · ${shipToContact.eMailAddress}` : ""}` : null
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <SystemSection d={d} onUpdate={onUpdate} />
      </div>

      {/* ── Buying options — revealed once a product is picked ──────────── */}
      {d.partNum && (
        <div className="qw-fade" style={{ marginTop: 24 }}>
          <div style={{ height: 1, background: "#E2E8F0", margin: "0 0 16px" }} />
          <h2 className="qw-h2" style={{ fontSize: 17, marginBottom: 2 }}>Buying options</h2>

          {locked && (
            <div className="qw-info qw-info-emerald" style={{ marginTop: 10, fontSize: 13 }}>
              Reorder parts are on — payment is locked to <strong>Pay now · single invoice (Net&nbsp;30)</strong>. Other options are disabled.
            </div>
          )}

          {/* Desktop: rows sit side by side in a grid (.qw-radio-rows) so the
              section doesn't leave a dead right half; phones stack them. The
              -3/-2 modifier matches the row count so column widths fit the
              cards ("Pay now · Pay Over Time" is wider than "Single · Multi"). */}
          <div className={`qw-radio-rows ${payMode === "now" ? "qw-radio-rows-3" : "qw-radio-rows-2"}`}>
            <RadioRow
              name="qw-buying-option"
              label="Buying option"
              options={[
                { value: "now", label: "Pay now" },
                { value: "pot", label: "Pay Over Time" },
              ]}
              value={payMode}
              disabledValues={locked ? ["pot"] : []}
              onChange={(v) => onUpdatePlan(v === "pot" ? "POT" : "N30")}
            />

            {payMode === "now" && (
              <>
                <RadioRow
                  name="qw-shipment-mode"
                  label="Shipment"
                  hint={shipmentMode === "multi"
                    ? "Multi → Multi-Shipment: one system split into deliveries, each ordered by its own PO."
                    : "Single → the whole quote ships at once."}
                  options={[
                    { value: "single", label: "Single" },
                    { value: "multi",  label: "Multi" },
                  ]}
                  value={shipmentMode}
                  disabledValues={locked ? ["multi"] : []}
                  onChange={(v) => onUpdatePlan(v === "multi" ? "MSP" : "N30")}
                />
                <RadioRow
                  name="qw-invoice-mode"
                  label="Invoices"
                  hint={cat === "MSP"
                    ? "One invoice per shipment — follows automatically from multi-shipment."
                    : invoiceMode === "multi"
                      ? "Multi → Balanced Split: same-day split into equal invoices."
                      : "Single → Net 30: pay in full within 30 days."}
                  options={[
                    { value: "single", label: "Single" },
                    { value: "multi",  label: "Multi" },
                  ]}
                  value={invoiceMode}
                  // You cannot invoice per shipment without shipping per
                  // shipment (and vice versa), so multi-shipment fixes this row.
                  disabledValues={cat === "MSP" ? ["single", "multi"] : (locked ? ["multi"] : [])}
                  onChange={(v) => onUpdatePlan(v === "multi" ? "BK" : "N30")}
                />
              </>
            )}

            {payMode === "pot" && (
              <RadioRow
                name="qw-returnable"
                label="Returnable"
                hint={returnable
                  ? "Returnable → Consign-to-Own: lease-to-own with a monthly ceiling."
                  : "Non-returnable → Pay-Over-Time: split into 2–36 monthly payments."}
                options={[
                  { value: "nonret", label: "Non-returnable" },
                  { value: "ret",    label: "Returnable" },
                ]}
                value={returnable ? "ret" : "nonret"}
                onChange={(v) => onUpdatePlan(v === "ret" ? "CTO" : "POT")}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Plan details + calculations for the resolved plan ───────────── */}
      {d.partNum && (
        <div className="qw-fade" style={{ marginTop: 24 }}>
          <div style={{ height: 1, background: "#E2E8F0", margin: "0 0 16px" }} />
          <PlanDetailsSection d={d} onUpdate={onUpdate} />
        </div>
      )}

      <div className="qw-actions">
        <button className="qw-btn qw-btn-ghost" onClick={onBackToCart}><FaChevronLeft /> Back</button>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {editingIndex == null && <button className="qw-btn qw-btn-outline" disabled={commitDisabled} title={commitHint} onClick={() => onCommit(true)}>Add + new line</button>}
          <button className="qw-btn qw-btn-primary" disabled={commitDisabled} title={commitHint} onClick={() => onCommit(false)}>{editingIndex != null ? (editDirty ? "Save changes" : "Next") : "Add to cart"} <FaChevronRight /></button>
        </div>
      </div>
    </div>
  );
}

function SystemSection({ d, onUpdate }) {
  const lastFetchedRef = useRef(null);
  const boms = d.reorderBoms || [];
  const selectedCount = boms.filter((b) => b.selected).length;
  // Multi-select picker: open/closed + its own filter box. Selections are applied
  // to the draft as they are ticked; OK just closes the panel, so nothing is lost
  // if the rep clicks away.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  // Shown when a negative quantity is typed; the entry itself is not kept.
  const [qtyError, setQtyError] = useState("");
  const visibleBoms = boms
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => {
      const q = pickerQuery.trim().toLowerCase();
      if (!q) return true;
      return `${b.partNum} ${b.partDescription || ""} ${b.systemsLabel || ""}`.toLowerCase().includes(q);
    });

  // Individual-components searches the SERVER, not the loaded page. The catalogue
  // is ~730 parts and the first fetch only brings 50 — filtering those 50 locally
  // would make the other ~680 unreachable, which is the exact problem this mode
  // exists to solve. Debounced, and already-ticked parts are merged back in so a
  // new search never silently drops a selection.
  const searchDebounce = useRef(null);
  // Always-current view of the picked parts for the async search callback.
  const bomsRef = useRef(boms);
  bomsRef.current = boms;
  useEffect(() => {
    if (!isIndividualParts(d)) return;
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      const q = pickerQuery.trim();
      fetch(`${API_URL}/api/Part/reorder-parts/search?q=${encodeURIComponent(q)}&top=1000`,
        { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)))
        .then((body) => {
          const rows = (body?.parts || []).map((b) => ({
            partNum: b.partNum || "",
            partDescription: b.partDescription || "",
            bomType: "",
            qtyPer: 1,
            unitPrice: Number(b.unitPrice ?? 0) || 0,
            systemsLabel: b.systemsLabel || "",
            quantity: 1,
            selected: false,
          })).filter((b) => b.partNum);
          // onUpdate takes a PLAIN PATCH (updateDraft spreads it) — never a
          // function. Read the live selections off a ref so a result landing
          // after a tick cannot resurrect a stale list.
          const kept = (bomsRef.current || []).filter((b) => b.selected);
          const keptKeys = new Set(kept.map((b) => b.partNum.toUpperCase()));
          const fresh = rows.filter((b) => !keptKeys.has(b.partNum.toUpperCase()));
          onUpdate({ reorderBoms: [...kept, ...fresh] });
        })
        .catch(() => {});
    }, 300);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerQuery]);
  const reorderOnly = isReorderOnly(d);
  // Reorder-only lines must carry at least one selected part — otherwise the
  // line quotes nothing. Block "Next" until the rep has picked one.
  const reorderOnlyIncomplete = reorderOnly && selectedCount === 0;

  // The scope radios stand in for the Quantity box while the line quotes parts
  // only — two doors to one fact is exactly how a rep types 1 back in and
  // silently leaves the mode. They only stand in while they are actually on
  // screen, so a parts-only line can never end up with neither control.
  const scopeControlVisible = !!d.reorder && !isIndividualParts(d);
  const showQuantity = !isIndividualParts(d) && !(scopeControlVisible && reorderOnly);
  // Multi-shipment quotes exactly one system (the emerald note under the plan):
  // the Quantity box is shown disabled at 1, and this keeps the DRAFT at 1 too,
  // so a 2 typed under another plan can never travel to /api/Msp as a 2 (the API
  // refuses it anyway - D-040 addendum 29 / Issue 23).
  const mspFixedQty = planCategory(d) === "MSP" && !reorderOnly;
  useEffect(() => {
    if (mspFixedQty && Number(d.quantity) !== 1) onUpdate({ quantity: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mspFixedQty, d.quantity]);

  // Load the system's BOM when "reorder parts" is on (or the system changes
  // while it's on). Same endpoint as /product-bom/{partNum}: GET /api/Part/{partNum}.
  useEffect(() => {
    if (!d.reorder || !d.partNum) return;
    if (lastFetchedRef.current === d.partNum) return;
    // Already loaded (e.g. re-entering edit mode) — keep the user's selections.
    if ((d.reorderBoms || []).length > 0) { lastFetchedRef.current = d.partNum; return; }
    lastFetchedRef.current = d.partNum;
    onUpdate({ reorderLoading: true, reorderError: null });

    // Two sources, one shape. Individual-components pulls every reorder part in
    // the catalogue (each annotated with the system(s) it belongs to); a real
    // system pulls just that system's BOM. Both normalise into the same row so
    // the picker, the estimate and the submit builder stay source-agnostic.
    const individual = isIndividualParts(d);
    const url = individual
      ? `${API_URL}/api/Part/reorder-parts/search?q=&top=1000`
      : `${API_URL}/api/Part/${encodeURIComponent(d.partNum)}`;

    fetch(url, { headers: authHeaders() })
      // A system with no BOM answers 404 "No BOM data found…" — that is "this system has
      // no parts", not a failure: an empty list shows "No BOM components found for X."
      // (UI-QTE-061). Any other non-OK answer is still "Couldn't load BOM: HTTP n".
      .then((r) => (r.ok ? r.json() : (!individual && r.status === 404 ? [] : Promise.reject(`HTTP ${r.status}`))))
      .then((body) => {
        const rows = individual ? (body?.parts || []) : body;
        const list = (Array.isArray(rows) ? rows : [])
          .map((b) => {
            if (individual) {
              const part = b.partNum || b.PartNum || "";
              return {
                partNum: part,
                partDescription: b.partDescription || b.PartDescription || "",
                bomType: "",
                // qtyPer is per-(part, system) so it is meaningless without a
                // parent — a cross-system pick starts at 1.
                qtyPer: 1,
                unitPrice: Number(b.unitPrice ?? b.UnitPrice ?? 0) || 0,
                systemsLabel: b.systemsLabel || b.SystemsLabel || "",
                quantity: 1,
                selected: false,
              };
            }
            const part = b.mtlPartNum || b.MtlPartNum || "";
            const qtyPer = Number(b.qtyPer ?? b.QtyPer ?? 1) || 1;
            return {
              partNum: part,
              partDescription: b.mtlPartDescription || b.MtlPartDescription || "",
              bomType: b.bomType || b.BomType || "",
              qtyPer,
              unitPrice: Number(b.unitPrice ?? b.UnitPrice ?? 0) || 0,
              systemsLabel: "",
              quantity: qtyPer,
              selected: false,
            };
          })
          .filter((b) => b.partNum)
          // The same component can arrive more than once — three BOM variants
          // per system on the BOM path, and the picker keys rows by index, so a
          // duplicate would let the rep quote one part twice.
          .filter((b, i, all) => all.findIndex((x) => x.partNum.toUpperCase() === b.partNum.toUpperCase()) === i);
        onUpdate({ reorderBoms: list, reorderLoading: false });
      })
      .catch((e) => onUpdate({ reorderBoms: [], reorderLoading: false, reorderError: String(e) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.reorder, d.partNum]);

  const toggleReorder = (on) => {
    if (on) {
      lastFetchedRef.current = null;                 // force a fresh BOM fetch
      onUpdate({ reorder: true, paymentPlan: { planCode: "N30" } }); // lock to Net 30
    } else {
      // Unticking the parent while the line quotes parts only used to leave the
      // quantity at 0 with nothing on screen to explain it: the scope radios and
      // the parts picker both live inside `d.reorder`, so the rep was left with
      // an amber banner asking for a part they could no longer see, and Add line
      // disabled. Turning reorder off puts the system back on the line.
      onUpdate({
        reorder: false,
        reorderBoms: [],
        reorderError: null,
        ...(isReorderOnly(d)
          ? { quantity: restoredSystemQty(d), systemQtyBeforePartsOnly: null }
          : {}),
      });
    }
  };

  // Switching scope is a mode change, not a value edit, so it writes the SAME
  // patch as the two doors that already exist — the typed-0 branch on the
  // Quantity box and the Individual-components handler. Nothing downstream sees
  // a new kind of line. No BOM re-fetch: the system hasn't changed, only whether
  // it is being bought.
  const setLineScope = (scope) => {
    const partsOnly = scope === LINE_SCOPE.REORDER_PARTS_ONLY;
    if (partsOnly === isReorderOnly(d)) return;      // already there
    onUpdate(partsOnly
      ? {
          systemQtyBeforePartsOnly: d.quantity,      // so flipping back isn't a silent 1
          quantity: 0,
          reorder: true,
          paymentPlan: { planCode: "N30" },
        }
      : {
          quantity: restoredSystemQty(d),
          systemQtyBeforePartsOnly: null,
        });
  };

  const updateBom = (idx, patch) =>
    onUpdate({ reorderBoms: boms.map((b, i) => (i === idx ? { ...b, ...patch } : b)) });

  return (
    <>
      {/* System (part) + Quantity side by side — system flexes to fill, quantity
          stays a narrow fixed column. Wraps to stacked on narrow screens. */}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div className="qw-field" style={{ flex: "1 1 280px", minWidth: 240, marginBottom: 0 }}>
          <SearchableSelect
            config={{ ...AUTOCOMPLETE_PART, pinnedOptions: [INDIVIDUAL_OPTION] }}
            // The sentinel is an internal marker — show the rep the label, not
            // "__INDIVIDUAL__".
            value={isIndividualParts(d) ? INDIVIDUAL_OPTION.Part_PartNum : d.partNum}
            onChange={(val) => onUpdate({ partNum: (val || "").toUpperCase() })}
            onSelectItem={(item) => {
              // The pinned "Individual components" row switches the line to
              // reorder-only in ONE patch: qty 0 + reorder on + N30, exactly what
              // toggleReorder / the qty-0 branch do, so nothing downstream sees a
              // new kind of line. Clearing the preview matters — the previously
              // chosen system's pricing must not survive onto a line that no
              // longer quotes a system.
              if (item.__individual) {
                lastFetchedRef.current = null;
                onUpdate({
                  partNum: INDIVIDUAL_PARTS,
                  partDescription: "",
                  catalogPrice: 0,
                  quantity: 0,
                  reorder: true,
                  reorderBoms: [],
                  paymentPlan: { planCode: "N30" },
                  preview: null,
                  previewLoading: false,
                  previewError: null,
                  previewSuggestion: null,
                  ...contractVerdict(null),
                });
                return;
              }
              const code = (item.Part_PartNum || item.partNum || "").toUpperCase();
              onUpdate({
                partNum: code,
                partDescription: item.Part_PartDescription || "",
                catalogPrice: item.Part_UnitPrice || 0,
                // a new system invalidates the previously-loaded BOM
                reorderBoms: [],
                // leaving individual-components mode restores a real quantity
                ...(isIndividualParts(d) ? { quantity: 1, reorder: false } : {}),
              });
            }}
            placeholder="Type part # or system name (e.g. S9BLADE)…"
            variant="card"
            label="System (part) *"
            secondaryText={d.partDescription}
          />
        </div>

        {/* Card-styled to match the System field — eyebrow label + borderless
            input inside a qw-select-card shell (no chevron).
            Hidden in individual-components mode: there is no system on the line,
            so there is nothing to count. Each part carries its own quantity.
            Also hidden while the line quotes parts only — the scope radios below
            own that decision, and a 0 sitting in a box is the hack this control
            replaces. */}
        {showQuantity && (
        <div
          className={"qw-select-card qw-mono qw-qty-card" + (d.quantity !== "" && d.quantity != null ? " selected" : "")}
          style={{ padding: "10px 16px", cursor: "text" }}
        >
          <div className="qw-select-eyebrow">Quantity</div>
          <div className="qw-select-row">
            <input
              className="qw-select-input qw-mono"
              type="number"
              min={0}
              value={d.quantity}
              disabled={mspFixedQty}
              title={mspFixedQty ? "Multi-shipment quotes exactly one system - quantity does not apply." : undefined}
              onChange={(e) => {
                if (mspFixedQty) return;
                const v = e.target.value;
                // A negative quantity is refused here (and by the API): Epicor made a
                // -1 line a quantity-1, $0 line. 0 stays the reorder-only switch.
                if (v !== "" && Number(v) < 0) {
                  setQtyError("Quantity cannot be negative. Enter 1 or more (0 = reorder parts only).");
                  return;
                }
                if (v !== "" && !Number.isInteger(Number(v))) {
                  setQtyError("Quantity must be a whole number of systems, like 1 or 2.");
                  return;
                }
                setQtyError("");
                // Quantity 0 = "reorder parts only" — the system itself isn't
                // quoted. Turn reorder on (if off) so the rep can pick the parts
                // this line will contain, and lock the group to Net 30.
                if (v !== "" && Number(v) === 0 && !d.reorder) {
                  lastFetchedRef.current = null;
                  onUpdate({ quantity: v, reorder: true, paymentPlan: { planCode: "N30" } });
                } else {
                  onUpdate({ quantity: v });
                }
              }}
            />
          </div>
          {qtyError && (
            <div role="alert" style={{ marginTop: 4, fontSize: 11, color: "#B91C1C", fontFamily: "inherit" }}>{qtyError}</div>
          )}
        </div>
        )}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: "#94A3B8", lineHeight: 1.4 }}>
        {isIndividualParts(d)
          ? <>Quoting reorder parts on their own — pick them below. Each part carries its own quantity.</>
          : <>Need parts without a system? Choose <strong>Individual components</strong> at the top of the System list.</>}
      </div>

      {/* ── Reorder parts ─────────────────────────────────────────────────── */}
      {/* T-002: the two <span>s each picked up the global `span { margin-left:16px }`
          from App.css on top of the flex gap, so the title and its hint drifted apart
          and wrapped into ragged columns on phones. .qw-reorder-label resets that and
          drops the hint onto its own line under 992px. */}
      <div style={{ marginTop: 18 }}>
        <label className="qw-reorder-label" style={{ cursor: d.partNum ? "pointer" : "not-allowed", opacity: d.partNum ? 1 : 0.5 }}>
          <input type="checkbox" checked={!!d.reorder} disabled={!d.partNum} onChange={(e) => toggleReorder(e.target.checked)} />
          <span className="qw-reorder-title">Reorder parts</span>
          <span className="qw-reorder-hint">add BOM components as extra Net&nbsp;30 lines</span>
        </label>

        {d.reorder && (
          <>
            {/* ── What this line quotes ──────────────────────────────────────
                The reason this control exists: "system + parts" had no name.
                It was simply the absence of a typed 0, so the only state a rep
                could see was the deviation. Two cards name both outcomes.

                Nested under the checkbox that reveals it — indented and
                rule-marked (.qw-line-scope) so it reads as part of that
                question, not as a third top-level one. Hidden in
                individual-components mode for the same reason the Quantity card
                is: there is no system on the line to include or exclude. */}
            {scopeControlVisible && (
              <div className="qw-line-scope">
                <RadioRow
                  name="qw-line-scope"
                  label="What this line quotes"
                  options={[
                    { value: LINE_SCOPE.SYSTEM_AND_REORDER_PARTS, label: "System + reorder parts" },
                    { value: LINE_SCOPE.REORDER_PARTS_ONLY,       label: "Reorder parts only" },
                  ]}
                  value={lineScope(d)}
                  onChange={setLineScope}
                  // The part number lives in the hint, never in the card label:
                  // a label that grows with the code restacks the pair on a
                  // phone, and these two have to stay the same width.
                  hint={reorderOnly
                    ? <>Only the reorder parts are quoted. <strong>{d.partNum}</strong> itself won&rsquo;t be added to this quote.</>
                    : <><strong>{d.partNum}</strong> is quoted, plus the reorder parts you pick below.</>}
                />
              </div>
            )}

            {/* Says the one thing the scope radios don't: why the buying option
                went grey. Ticking Reorder parts is what locks the group to Net
                30 — parts-only doesn't add anything to that. */}
            <div className="qw-info qw-info-emerald" style={{ marginTop: 10, fontSize: 12.5 }}>
              Reorder parts are billed <strong>Net&nbsp;30</strong> — the buying option is locked to N30 for this line group.
            </div>
            <div style={{ marginTop: 12 }}>
              {d.reorderLoading && <div style={{ color: "#64748B", fontSize: 13 }}><Spinner animation="border" size="sm" /> Loading BOM…</div>}
              {d.reorderError && <div className="qw-info qw-info-amber" style={{ fontSize: 13 }}>Couldn't load BOM: {d.reorderError}</div>}
              {!d.reorderLoading && !d.reorderError && boms.length === 0 && (
                <div style={{ color: "#94A3B8", fontSize: 13 }}>No BOM components found for {d.partNum}.</div>
              )}
              {boms.length > 0 && (
                <div>
                  {/* Multi-select picker — checkboxes + a per-part quantity,
                      confirmed with OK. Replaces the old one-at-a-time dropdown:
                      a rep restocking a tray ticks eight parts in one pass
                      instead of running eight separate searches. Selections are
                      written to the draft as they are ticked, so OK only closes
                      the panel — clicking away loses nothing. */}
                  <button
                    type="button"
                    onClick={() => setPickerOpen((o) => !o)}
                    className="qw-select-card"
                    style={{ width: "100%", textAlign: "left", padding: "10px 16px", cursor: "pointer", background: "#fff" }}
                  >
                    <div className="qw-select-eyebrow">Reorder parts</div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 600, color: "#0F172A", fontSize: 13.5 }}>
                        {selectedCount === 0
                          ? "Choose parts…"
                          : `${selectedCount} part${selectedCount === 1 ? "" : "s"} selected · ${fmtMoney(reorderEstimate(d))}`}
                      </span>
                      <span style={{ color: "#94A3B8", fontSize: 12, flexShrink: 0 }}>{pickerOpen ? "Close" : "Edit"}</span>
                    </div>
                  </button>

                  {pickerOpen && (
                    <div style={{ marginTop: 8, border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
                      <div style={{ padding: "9px 12px", borderBottom: "1px solid #EDF0F5" }}>
                        <input
                          className="qw-input"
                          placeholder={isIndividualParts(d) ? "Search every system…" : "Filter BOM parts…"}
                          value={pickerQuery}
                          onChange={(e) => setPickerQuery(e.target.value)}
                          style={{ width: "100%" }}
                          autoFocus
                        />
                      </div>

                      {/* Tall enough to browse, not just peek. Rows run 2-3 lines
                          (part / parent systems / price), so 300px showed barely
                          three of them and the catalogue read as "almost empty".
                          Capped against the viewport so it still fits a phone. */}
                      <div style={{ maxHeight: "min(60vh, 560px)", overflowY: "auto" }}>
                        {visibleBoms.length === 0 ? (
                          <div style={{ padding: 16, fontSize: 13, color: "#94A3B8", textAlign: "center" }}>
                            No parts match “{pickerQuery}”.
                          </div>
                        ) : (
                        /* A TABLE, and the table is the point.
                           The row used to be a flexbox whose code cell was
                           `flex: 1`, so the code ate the width and the price sat
                           ~1300px away from the part it prices. Packing the price
                           up against the code fixes that distance and breaks
                           something else: part numbers are not the same length,
                           so each row's price starts wherever THAT row's code
                           happened to end, and the money column zigzags.
                           Both problems have one cause — each row deciding its
                           own widths. A table column is sized to content ACROSS
                           every row, which is the one layout primitive that packs
                           the fields together AND keeps them in a straight line.
                           Every real column shrinks to content; the trailing
                           spacer takes all the leftover width, so slack collects
                           at the far right instead of opening up between the
                           fields. */
                        <table className="qw-bom-table"><tbody>
                        {visibleBoms.map(({ b, i }) => (
                          <tr
                            key={`bom-${b.partNum}-${i}`}
                            onClick={() => updateBom(i, { selected: !b.selected })}
                            className={b.selected ? "selected" : undefined}
                          >
                            <td className="qw-bom-cell-check">
                              <input
                                type="checkbox"
                                checked={!!b.selected}
                                onChange={(e) => updateBom(i, { selected: e.target.checked })}
                                onClick={(e) => e.stopPropagation()}
                                className="qw-bom-check"
                              />
                            </td>
                            <td className="qw-bom-code qw-mono">{b.partNum}</td>
                            {/* Description, and which system(s) the part belongs
                                to — the whole reason individual-components
                                exists. Empty for BOM parts, so this column
                                collapses and the price lands beside the code. */}
                            <td className="qw-bom-meta">
                              {b.partDescription && <div className="qw-bom-desc">{b.partDescription}</div>}
                              {b.systemsLabel && <div className="qw-bom-systems">{b.systemsLabel}</div>}
                            </td>
                            {/* unit price is reference; the EXTENDED amount is
                                the one that answers "what does this cost me",
                                so it sits after the quantity box and moves the
                                moment the rep types. Without it the only
                                feedback on a quantity change was the running
                                total in the header, several rows away. */}
                            <td className="qw-bom-price qw-mono">{fmtMoney(b.unitPrice)}</td>
                            <td className="qw-bom-cell-qty">
                              <input
                                className="qw-input qw-mono qw-bom-qty"
                                type="number"
                                min={1}
                                value={b.quantity}
                                disabled={!b.selected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  // Negative reorder quantities are not kept (see sendQty).
                                  if (e.target.value !== "" && Number(e.target.value) < 0) return;
                                  updateBom(i, { quantity: e.target.value });
                                }}
                                aria-label={`Quantity for ${b.partNum}`}
                              />
                            </td>
                            <td className="qw-bom-ext qw-mono">{fmtMoney(bomExtended(b))}</td>
                            <td className="qw-bom-spacer" />
                          </tr>
                        ))}
                        </tbody></table>
                        )}
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC" }}>
                        <span style={{ fontSize: 12, color: "#64748B" }}>
                          {selectedCount} selected · {fmtMoney(reorderEstimate(d))}
                        </span>
                        <button
                          type="button"
                          className="qw-btn qw-btn-primary qw-btn-sm"
                          onClick={() => { setPickerOpen(false); setPickerQuery(""); }}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Closed-state summary so the picked parts stay visible
                      without reopening the panel. */}
                  {!pickerOpen && selectedCount > 0 && (
                    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8 }}>
                      {boms.map((b, i) => ({ b, i })).filter((x) => x.b.selected).map(({ b, i }) => (
                        <div key={`sel-${b.partNum}-${i}`} className="qw-select-card selected" style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", cursor: "default" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="qw-select-eyebrow">Reorder part</div>
                            <div className="qw-mono" style={{ fontWeight: 600, color: "#0F172A", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={b.partNum}>{b.partNum}</div>
                          </div>
                          <span className="qw-mono" style={{ fontSize: 12, color: "#64748B", flexShrink: 0 }}>× {b.quantity}</span>
                          <button type="button" onClick={() => updateBom(i, { selected: false })} title="Remove"
                            style={{ flexShrink: 0, border: "none", background: "transparent", color: "#94A3B8", fontSize: 18, lineHeight: 1, cursor: "pointer", padding: "0 2px" }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCount === 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#94A3B8" }}>
                      No parts added yet — open the picker above and tick what you need.
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {reorderOnlyIncomplete && (
        <div className="qw-info qw-info-amber" style={{ marginTop: 12, fontSize: 13 }}>
          This line quotes reorder parts only — pick at least one before adding it.
        </div>
      )}
    </>
  );
}

// Plan details + calculations for the resolved plan (N30 / POT / BK / CTO) —
// rendered inline below the buying-option radios.
function PlanDetailsSection({ d, onUpdate }) {
  const cat = planCategory(d);
  // Rep discount is only permitted on Net-30 plans (matches backend rule).
  // If the plan is anything else, make sure no stale discount lingers.
  useEffect(() => {
    if (cat !== "N30" && d.repDiscountPercent) onUpdate({ repDiscountPercent: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);
  // (Plan-name intro banner — "Net 30 — single invoice" / "Balanced Split" /
  //  "Pay-Over-Time" / "Consign-to-Own" — hidden for now: the buying-option
  //  radio hints already name the resolved plan.)

  return (
    <>
      {/* Rep discount is not offered at quote creation — lines always start at
          0%. Applying a discount is an Edit-Quote affordance gated on action
          00079 (CustomerService / IT). */}

      <div style={{ marginTop: 18 }}>
        {cat === "POT" && <PotControls line={d} lineKey={d._key} suggestion={d.previewSuggestion || d.preview?.suggestion} onUpdate={onUpdate} />}
        {cat === "BK"  && <BkControls  line={d} lineKey={d._key} previewPricing={d.preview} onUpdate={onUpdate} />}
        {cat === "CTO" && <CtoControls line={d} lineKey={d._key} onUpdate={onUpdate} />}
        {cat === "MSP" && <MspControls line={d} lineKey={d._key} onUpdate={onUpdate} />}
        {cat === "N30" && <TradeInToggle line={d} onUpdate={onUpdate} />}
      </div>

      <LinePreview line={d} />
      <ReorderPartsSummary line={d} />
    </>
  );
}

// N30 trade-in is optional — show an "add" affordance, then the existing
// inline TradeInBlock form once a trade-in object exists on the line.
function TradeInToggle({ line, onUpdate }) {
  if (!line.tradeIn) {
    return (
      <button className="qw-btn qw-btn-outline" onClick={() => onUpdate({ tradeIn: { condition: "Good" } })}>
        <FaPlus /> Add trade-in (optional)
      </button>
    );
  }
  return <TradeInBlock tradeIn={line.tradeIn} onUpdate={(t) => onUpdate({ tradeIn: t })} />;
}

// Selected reorder parts + their estimate. Rendered independently of the
// system's server preview (the estimate is client-side: unitPrice × qty), so
// it still shows even when live pricing for the system is unavailable.
function ReorderPartsSummary({ line }) {
  const boms = selectedReorderBoms(line);
  if (boms.length === 0) return null;
  const subtotal = reorderEstimate(line);
  // Pre-tax system portion (matches the cart / review; tax applied at order).
  const sysTotal = (Number(line.preview?.totalPrice) || 0) - (Number(line.preview?.taxAmount) || 0);
  const reorderOnly = isReorderOnly(line);
  return (
    <div className="qw-card-flat" style={{ padding: 14, marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span className="qw-pill qw-pill-emerald">Reorder parts · N30</span>
        <span style={{ fontSize: 12, color: "#94A3B8" }}>{boms.length} line{boms.length === 1 ? "" : "s"}</span>
      </div>
      {boms.map((b, i) => (
        <div key={`${b.partNum}-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
          <span className="qw-mono" style={{ color: "#475569" }}>{b.partNum} <span style={{ color: "#94A3B8" }}>· qty {b.quantity}</span></span>
          <span className="qw-mono" style={{ color: "#475569" }}>{fmtMoney((Number(b.unitPrice) || 0) * (Number(b.quantity) || 0))}</span>
        </div>
      ))}
      {/* Footer total band — mirrors the "System subtotal" row in LinePreview
          (bleeds to the card edges via negative side margins). */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
        margin: "8px -14px 0", padding: "9px 14px",
        borderTop: "1px solid #E2E8F0", background: "#F8FAFC",
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>Reorder subtotal (est.)</span>
        <span className="qw-mono" style={{ fontWeight: 700, color: "#0052A5", fontSize: 16, lineHeight: 1.1 }}>{fmtMoney(subtotal)}</span>
      </div>
      {!reorderOnly && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
          marginTop: 10, padding: "10px 14px",
          background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 10,
        }}>
          <span style={{ fontWeight: 700, color: "#0F172A", fontSize: 14 }}>Line estimate (system + reorder)</span>
          <span className="qw-mono" style={{ fontWeight: 700, color: "#4F46E5", fontSize: 20, lineHeight: 1.1 }}>{fmtMoney(sysTotal + subtotal)}</span>
        </div>
      )}
      {!reorderOnly && sysTotal === 0 && (
        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>System price not previewed — finalized on submit; estimate shows reorder parts only.</div>
      )}
      {reorderOnly && (
        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Reorder-only line — the system is not quoted. Parts are priced by the server on submit.</div>
      )}
    </div>
  );
}

// (The old Preview & Review sub-step was removed — the Add-Line flow now commits
// straight from the Plan details sub-step.)

// =============================================================================
// Cart — committed lines, with edit / remove and the running total.
// =============================================================================
// =============================================================================
// ReviewStep — the cart and the submit form merged into one final step.
// Lists each line once (Edit / Remove / Add new line), then the submit fields,
// pricing roll-up and the Create button. Replaces the old Cart + Submit steps.
// =============================================================================
function ReviewStep({
  territoryID, territoryName, salesRepCode, salesRepName,
  custID, customerName, poNum, setPoNum, comments, setComments,
  reportStyle, setReportStyle, custContact, shipToContact,
  cart, cartTotal, quoteCount, rollup,
  canSubmit, submitting, submitResult,
  onAddLine, onEdit, onRemove, onBack, onSubmit, onCreateAnother, onViewQuote,
}) {
  const submitted = submitResult?.success === true;
  // Quote totals are pre-tax (matching the Epicor quote); tax is only applied
  // when the quote becomes an order — surfaced so the total isn't mistaken final.
  const cartTax = cart.reduce((s, l) => s + (Number(l.preview?.taxAmount) || 0), 0);
  // Same predicate the parent's canSubmit uses — this only names the lines.
  const blockedLines = cart.filter(isContractBlocked);
  return (
    <div>
      {/* Header — cart summary + add-line + running total */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <h2 className="qw-h2">Review &amp; submit</h2>
          <p className="qw-sub">
            {submitted
              ? "Quotes created in Epicor — see below."
              : <>
                  {cart.length} line item{cart.length === 1 ? "" : "s"}
                  {customerName ? ` · ${customerName}` : ""}
                  {cart.length ? ` · ${quoteCount} Epicor quote${quoteCount === 1 ? "" : "s"}` : ""}
                </>}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
          {submitted
            ? <span className="qw-pill qw-pill-emerald"><FaCheckCircle style={{ marginRight: 4 }} /> Submitted</span>
            : cart.length > 0 && (
                <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={onAddLine}><FaPlus /> Add new line</button>
              )}
          {cart.length > 0 && (
            <div style={{ textAlign: "right" }}>
              <div className="qw-eyebrow">Cart total (pre-tax)</div>
              <div className="qw-mono" style={{ fontSize: 24, fontWeight: 700, color: "#0F172A" }}>{fmtMoney(cartTotal)}</div>
              {cartTax > 0 && (
                <>
                  <div style={{ fontSize: 11.5, color: "#94A3B8", fontStyle: "italic", marginTop: 2 }}>+ est. tax at order {fmtMoney(cartTax)}</div>
                  <div className="qw-mono" style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginTop: 2 }}>Est. total at order {fmtMoney(estTotalAtOrder(cartTotal, rollup?.tradeInCredit, cartTax))}</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {cart.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#64748B" }}>
          <p style={{ marginBottom: 14 }}>Your cart is empty.</p>
          <button className="qw-btn qw-btn-primary" onClick={onAddLine}><FaPlus /> Add first line</button>
        </div>
      ) : (
        <>
          {/* Lines — full pricing detail + Edit/Remove, listed once */}
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Line details</div>
          <div className="qw-stack">
            {cart.map((l, i) => (
              <CartLineRow key={l._key || i} line={l} index={i} onEdit={() => onEdit(i)} onRemove={() => onRemove(i)} readOnly={submitted} />
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: "#94A3B8", margin: "12px 0 16px" }}>
            Line and cart totals are pre-tax, matching the Epicor quote. Estimated sales tax is added when the quote becomes an order.
          </div>

          {/* Summary */}
          <div className="qw-card-flat qw-pad" style={{ marginBottom: 16 }}>
            <div className="qw-eyebrow" style={{ marginBottom: 10 }}>Summary</div>
            <div className="qw-grid-2" style={{ fontSize: 13, gap: 10 }}>
              <SummaryItem label="Customer" value={customerName} sub={custID} />
              <SummaryItem label="Sales rep" value={salesRepName || salesRepCode} sub={salesRepName ? salesRepCode : null} />
              <SummaryItem label="Territory" value={territoryID || "—"} sub={territoryName} />
              <SummaryItem label="Lines → quotes" value={`${cart.length} line${cart.length === 1 ? "" : "s"} → ${quoteCount} quote${quoteCount === 1 ? "" : "s"}`} />
              <SummaryItem
                label="Customer contact"
                value={custContact ? (custContact.name || `Contact ${custContact.conNum}`) : "—"}
                sub={custContact ? `ConNum ${custContact.conNum}` : null}
              />
              <SummaryItem
                label="Ship-to contact"
                value={shipToContact ? (shipToContact.name || `Contact ${shipToContact.conNum}`) : "—"}
                sub={shipToContact ? `ConNum ${shipToContact.conNum} · ShipTo ${shipToContact.shipToNum}` : null}
              />
            </div>
          </div>

          {/* PO + comments + report style (editable until submitted) */}
          {!submitted && (
            <div className="qw-grid-2" style={{ marginBottom: 16 }}>
              <div>
                <label className="qw-label">Customer PO (optional)</label>
                <input className="qw-input" value={poNum} onChange={(e) => setPoNum(e.target.value)} placeholder="e.g. PO-2026-0042" />
              </div>
              <div>
                <label className="qw-label">Comments (optional)</label>
                <input className="qw-input" value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Header note for the quote" />
              </div>
              <div>
                <label className="qw-label">Report style (emailed to recipients)</label>
                <select className="qw-input" value={reportStyle} onChange={(e) => setReportStyle(e.target.value)}>
                  {REPORT_STYLES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                  On create, the quote PDF is emailed to the sales rep, distributor &amp; customer contact in this style.
                </div>
              </div>
            </div>
          )}

          {/* Pricing roll-up */}
          {rollup.totalLines > 0 && (
            <div className="qw-card-flat qw-pad" style={{ marginBottom: 16 }}>
              <div className="qw-eyebrow" style={{ marginBottom: 10 }}>Pricing summary</div>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <tbody>
                  <RollupRow label="Selling Price" value={fmtMoney(rollup.sellingPrice)} />
                  <RollupRow label="System Discount" value={`(${fmtMoney(rollup.systemDiscount)})`} muted />
                  <RollupRow label="Rep Discount" value={`(${fmtMoney(rollup.repDiscountAmount)})`} muted />
                  {rollup.interestAmount > 0 && <RollupRow label="Interest" value={fmtMoney(rollup.interestAmount)} />}
                  {rollup.totalInvoiceFees > 0 && <RollupRow label="Invoice Fees" value={fmtMoney(rollup.totalInvoiceFees)} />}
                  {rollup.customerExtras > 0 && <RollupRow label="Shipping + misc (spread over the months)" value={fmtMoney(rollup.customerExtras)} />}
                  {rollup.reorderTotal > 0 && <RollupRow label="Reorder parts" value={fmtMoney(rollup.reorderTotal)} />}
                  <tr><td colSpan={2}><div style={{ height: 1, background: "#E2E8F0", margin: "8px 0" }} /></td></tr>
                  <RollupRow label="Grand Total" value={fmtMoney(rollup.totalPrice)} bold />
                  {rollup.tradeInCredit > 0 && <RollupRow label="Trade-in credit" value={`(${fmtMoney(rollup.tradeInCredit)})`} muted />}
                  {rollup.tradeInCredit > 0 && <RollupRow label="After trade-in (pre-tax)" value={fmtMoney(rollup.totalPrice - rollup.tradeInCredit)} bold />}
                  {rollup.taxAmount > 0 && (
                    <>
                      <tr>
                        <td style={{ padding: "6px 0 0", fontSize: 11.5, color: "#94A3B8", fontStyle: "italic" }}>
                          + est. sales tax (applied when converted to an order)
                        </td>
                        <td style={{ padding: "6px 0 0", textAlign: "right", fontSize: 11.5, color: "#94A3B8", fontStyle: "italic" }} className="qw-mono">
                          {fmtMoney(rollup.taxAmount)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: "8px 0 0", fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
                          Est. total at order (incl. tax)
                        </td>
                        <td style={{ padding: "8px 0 0", textAlign: "right", fontSize: 13, fontWeight: 700, color: "#0F172A" }} className="qw-mono">
                          {fmtMoney(estTotalAtOrder(rollup.totalPrice, rollup.tradeInCredit, rollup.taxAmount))}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
              {rollup.pricedLines < rollup.totalLines && (
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8, fontStyle: "italic" }}>
                  Showing {rollup.pricedLines} of {rollup.totalLines} priced lines.
                </div>
              )}
            </div>
          )}

          {!submitted && blockedLines.length > 0 && (
            <div className="qw-info qw-info-amber" style={{ marginBottom: 16, background: "#FEF2F2", color: "#991B1B" }}>
              <div>
                <strong>
                  {blockedLines.length === 1
                    ? "One line can't be quoted to this customer."
                    : `${blockedLines.length} lines can't be quoted to this customer.`}
                </strong>{" "}
                Their price contract refuses {blockedLines.length === 1 ? "it" : "them"}, and Epicor
                would reject the quote for the same reason — edit or remove{" "}
                {blockedLines.length === 1 ? "that line" : "those lines"} to submit.
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {blockedLines.map((l) => (
                    <li key={l._key} style={{ overflowWrap: "anywhere" }}>
                      <span className="qw-mono">{l.partNum}</span> — {l.previewError || l.contractNote}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {submitResult && !submitResult.success && (
            <div className="qw-info qw-info-amber" style={{ marginBottom: 16, background: "#FEF2F2", color: "#991B1B" }}>
              <div><strong>Quote creation failed.</strong> {submitResult.error}</div>
            </div>
          )}
          {submitted && <CreatedQuotes result={submitResult} onCreateAnother={onCreateAnother} onViewQuote={onViewQuote} />}

          {!submitted && (
            <div className="qw-actions">
              <button className="qw-btn qw-btn-ghost" onClick={onBack} disabled={submitting}><FaChevronLeft /> Back</button>
              <button className="qw-btn qw-btn-success" disabled={!canSubmit} onClick={onSubmit}>
                {submitting ? <><Spinner animation="border" size="sm" /> Creating…</> : "Create Quote"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// ContactsPicker — pick a customer (Sold-To) contact and a ship-to contact.
// These are two separate Epicor lists, not one list shown twice: a contact
// belongs to (CustNum, ShipToNum), so a customer-level contact is never a valid
// ship-to attention and vice versa. The chosen ConNums ride along in the quote
// comments. Rendered inline inside the combined SetupStep.
// =============================================================================
function ContactsPicker({
  custContacts, shipToContacts, loading, error,
  custContact, setCustContact, shipToContact, setShipToContact,
}) {
  // ConNum repeats across ship-to locations, so identity is the pair.
  const sameContact = (a, b) =>
    a != null && b != null &&
    a.conNum === b.conNum &&
    String(a.shipToNum ?? "") === String(b.shipToNum ?? "");

  const renderCard = (c, selected, setSelected, kind) => (
    <button
      key={`${kind}-${c.shipToNum || "-"}-${c.conNum}`}
      className={`qw-opt-card ${sameContact(selected, c) ? "selected" : ""}`}
      onClick={() => setSelected(c)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: "#0F172A" }}>{c.name || `Contact ${c.conNum}`}</div>
          {c.eMailAddress ? <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, overflowWrap: "anywhere" }}>{c.eMailAddress}</div> : null}
        </div>
        <span className="qw-pill qw-pill-slate qw-mono" style={{ flexShrink: 0 }}>ConNum {c.conNum}</span>
      </div>
    </button>
  );

  const renderList = (list, selected, setSelected, kind, emptyText) => {
    if (loading) {
      return <div style={{ color: "#64748B", fontSize: 13, padding: "10px 0" }}><Spinner animation="border" size="sm" /> Loading contacts…</div>;
    }
    if (error) {
      return <div className="qw-info qw-info-amber" style={{ fontSize: 13 }}>Couldn't load contacts: {error}</div>;
    }
    if (!list.length) {
      return <div style={{ color: "#94A3B8", fontSize: 13, padding: "10px 0" }}>{emptyText}</div>;
    }
    // Ship-to contacts are grouped under their location — without the heading a
    // list of repeated ConNums from different addresses reads as duplicates.
    if (kind === "ship") {
      const byLocation = list.reduce((acc, c) => {
        const key = String(c.shipToNum ?? "");
        (acc[key] = acc[key] || []).push(c);
        return acc;
      }, {});
      return (
        <div className="qw-stack">
          {Object.keys(byLocation).sort().map((loc) => (
            <div key={`loc-${loc}`}>
              <div className="qw-eyebrow qw-mono" style={{ marginBottom: 6 }}>ShipTo {loc}</div>
              <div className="qw-stack">
                {byLocation[loc].map((c) => renderCard(c, selected, setSelected, kind))}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <div className="qw-stack">{list.map((c) => renderCard(c, selected, setSelected, kind))}</div>;
  };

  return (
    <div className="qw-grid-2">
      <div>
        <label className="qw-label">Customer contact</label>
        {renderList(custContacts, custContact, setCustContact, "cust", "No customer-level contacts on file.")}
      </div>
      <div>
        <label className="qw-label">Ship-to contact</label>
        {renderList(shipToContacts, shipToContact, setShipToContact, "ship", "No ship-to contacts on file.")}
      </div>
    </div>
  );
}


/* T-006: this was a <div> of three inline <span>s. Each one picked up the global
   `span { margin-left: 16px }` from App.css, so the label was indented, the value
   was shoved a random distance from it, and nothing lined up between rows. Worse,
   the spans wrapped as inline text — a long value pushed its "(sub)" onto the next
   line, dangling at the far left (e.g. "kapil main tr admin 1 / (D57-R02)").
   It is now a real 2-column label/value grid, so every row shares the same
   columns, and `sub` sits INSIDE the value cell so it can never dangle. */
export function SummaryItem({ label, value, sub }) {
  return (
    <div className="qw-sum-row">
      <span className="qw-sum-label">{label}</span>
      <span className="qw-sum-value">
        {value || "—"}
        {sub && <span className="qw-mono qw-sum-sub">({sub})</span>}
      </span>
    </div>
  );
}

// The cart's plan pill, in the Add Line screen's own words, so every line says how
// the customer pays (UI-QTE-098: N30 and named CTO lines had no pill at all once
// the plan-code pill was hidden):
//   • N30 → Pay now · Net 30          • BK  → Pay now · Split $X/inv
//   • POT → Pay over time · N months (or · budget $X/inv)
//   • CTO → Returnable · C3K plan (or · custom $X/mo)   • MSP → Multi-ship …
function planSelectionLabel(line) {
  const cat = planCategory(line);
  const pp = line.paymentPlan || {};
  if (cat === "POT") {
    if (line.potMode === "budget") {
      return pp.desiredPerInvoice ? `Pay over time · budget ${fmtMoney(pp.desiredPerInvoice)}/inv` : "Pay over time";
    }
    return pp.numberOfMonths ? `Pay over time · ${pp.numberOfMonths} months` : "Pay over time";
  }
  if (cat === "BK") {
    return pp.desiredPerInvoice ? `Pay now · Split ${fmtMoney(pp.desiredPerInvoice)}/inv` : "Pay now · Split";
  }
  if (cat === "CTO") {
    if (line.ctoMode === "custom") {
      return pp.customMonthlyAmount ? `Returnable · custom ${fmtMoney(pp.customMonthlyAmount)}/mo` : "Returnable";
    }
    return `Returnable · ${pp.namedCtoPlan || "C3K"} plan`;
  }
  if (cat === "MSP") {
    if (line.mspMode === "ceiling") {
      return pp.perShipmentCeiling ? `Multi-ship ≤ ${fmtMoney(pp.perShipmentCeiling)}/shipment` : "Multi-ship";
    }
    return pp.shipmentCount ? `Multi-ship × ${pp.shipmentCount}` : "Multi-ship";
  }
  if (cat === "N30") return "Pay now · Net 30";
  return null;
}

function CartLineRow({ line, index, onEdit, onRemove, readOnly }) {
  const p = line.preview;
  const reorderOnly = isReorderOnly(line);
  const planLabel = planSelectionLabel(line);
  // Pre-tax system portion (matches LinePreview / Review). When reorder parts
  // ride along, the headline shows just this SYSTEM subtotal and the line's
  // grand total (system + reorder) is broken out in its own band below — so the
  // number next to the system part doesn't silently include the reorder parts.
  const sysSubtotal = (Number(p?.totalPrice) || 0) - (Number(p?.taxAmount) || 0);
  const reorderSub = reorderEstimate(line);
  const hasReorder = selectedReorderBoms(line).length > 0;
  const headlineValue = reorderOnly ? reorderSub : sysSubtotal;
  return (
    <div className="qw-cart-line">
      {/* T-005: this top row is a 2-up flex (details | price + Edit/Remove). On a
          phone the price column still claimed ~200px, so the details column was
          squeezed to a ribbon — the part description wrapped mid-phrase and ran
          into the buttons. Under 992px .qw-cart-line-top stacks: details on their
          own full-width row, then price (left) and the buttons (right) beneath. */}
      <div className="qw-cart-line-top">
        <div className="qw-cart-line-main">
          <div className="qw-cart-line-pills">
            <span className="qw-pill qw-pill-slate">Line {index + 1}</span>
            {/* (Plan-code pill hidden for now, matching the Add Line screen.) */}
            {planLabel && <span className="qw-pill qw-pill-slate">{planLabel}</span>}
            {reorderOnly && <span className="qw-pill qw-pill-emerald">Reorder only</span>}
            {isContractBlocked(line) && <span className="qw-pill qw-pill-amber">Blocked by contract</span>}
            {line.repDiscountPercent > 0 && <span className="qw-pill qw-pill-amber">{line.repDiscountPercent}% disc.</span>}
          </div>
          <div className="qw-cart-line-desc">
            {line.partDescription || line.partNum || <em style={{ color: "#94A3B8", fontWeight: 400 }}>(no part)</em>}
            {/* UI-QR-N074: a cleared box read "qty" with no number; show the 1 that is sent. */}
            <span className="qw-cart-line-qty">{reorderOnly ? " · system not quoted" : ` · qty ${sendQty(line.quantity)}`}</span>
          </div>
          <div className="qw-mono qw-cart-line-part">
            {line.partNum}
            {p?.numberOfMonths
              ? ` · ${p.numberOfMonths}× ${fmtMoney(p.perInvoiceAmount)}`
              : planCategory(line) === "MSP" && p?.numberOfInvoices
                ? ` · ${p.numberOfInvoices} shipments × ${fmtMoney(p.perInvoiceAmount)}`
                : ""}
          </div>
        </div>
        <div className="qw-cart-line-side">
          {hasReorder && !reorderOnly && (
            <div className="qw-cart-line-eyebrow">System subtotal</div>
          )}
          <div className="qw-mono qw-cart-line-total">{fmtMoney(headlineValue)}</div>
          {!readOnly && (
            <div className="qw-cart-line-actions">
              <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={onEdit}>Edit</button>
              <button className="qw-btn qw-btn-danger qw-btn-sm" onClick={onRemove}>Remove</button>
            </div>
          )}
        </div>
      </div>

      {/* Why this line's price differs from the catalogue — the server's own
          sentence, shown again at sign-off. A blocked line shows the refusal. */}
      {(line.contractNote || isContractBlocked(line)) && (
        <div
          className={isContractBlocked(line) ? undefined : "qw-info-indigo"}
          style={{
            marginTop: 10, padding: "6px 10px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.45,
            overflowWrap: "anywhere",
            ...(isContractBlocked(line) ? { background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" } : {}),
          }}
        >
          <strong>{isContractBlocked(line) ? "Refused by contract · " : "Contract price · "}</strong>
          <span className="qw-mono">{line.contractNote || line.previewError}</span>
        </div>
      )}

      {/* Review's re-check re-priced this line (today's contract differs from
          the one it was added under) — say so, so the new figure isn't a surprise. */}
      {line.priceChangeNote && (
        <div style={{
          marginTop: 10, padding: "6px 10px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.45,
          overflowWrap: "anywhere", background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A",
        }}>
          {line.priceChangeNote}
        </div>
      )}

      {/* Reorder parts grouped under the system line. */}
      {selectedReorderBoms(line).length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #E2E8F0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span className="qw-pill qw-pill-emerald">Reorder parts · N30</span>
            <span style={{ fontSize: 12, color: "#94A3B8" }}>{selectedReorderBoms(line).length} extra line(s)</span>
          </div>
          {selectedReorderBoms(line).map((b, i) => (
            <div key={`${b.partNum}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0 3px 12px", fontSize: 13 }}>
              <span className="qw-mono" style={{ color: "#475569" }}>{b.partNum} <span style={{ color: "#94A3B8" }}>· qty {b.quantity}</span></span>
              <span className="qw-mono" style={{ color: "#475569" }}>{fmtMoney((Number(b.unitPrice) || 0) * (Number(b.quantity) || 0))}</span>
            </div>
          ))}
          {!reorderOnly && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 8, paddingTop: 8, borderTop: "1px solid #E2E8F0" }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>Line total (system + reorder)</span>
              <span className="qw-mono" style={{ fontWeight: 700, color: "#0052A5", fontSize: 16 }}>{fmtMoney(sysSubtotal + reorderSub)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreatedQuotes({ result, onCreateAnother, onViewQuote }) {
  const quotes = result.quotes || [];
  return (
    <div className="qw-card-flat qw-pad" style={{ border: "2px solid #A7F3D0", background: "#F0FDF4", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: 999, background: "#10B981", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
          <FaCheckCircle />
        </div>
        <div style={{ fontWeight: 700, color: "#065F46" }}>Quote{quotes.length === 1 ? "" : "s"} created</div>
      </div>
      {quotes.length > 0 ? quotes.map((q, i) => {
        const quoteNum = q.quoteNum || q.QuoteNum;
        const total = q.totalPrice ?? q.pricing?.totalPrice ?? q.pricing?.TotalPrice;
        const terms = q.termsCode || q.TermsCode || q.planCode || q.PlanCode;
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 12px", background: "#fff", borderRadius: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 700, color: "#0F172A" }}>Quote #{quoteNum}</div>
              <div className="qw-mono" style={{ fontSize: 12, color: "#64748B" }}>
                {terms ? `terms ${terms}` : ""}{q.lineCount ? ` · ${q.lineCount} line${q.lineCount === 1 ? "" : "s"}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {total != null && <div className="qw-mono" style={{ fontWeight: 700, color: "#0F172A" }}>{fmtMoney(total)}</div>}
              {quoteNum != null && (
                <button className="qw-btn qw-btn-primary qw-btn-sm" onClick={() => onViewQuote(quoteNum)}>
                  View quote <FaChevronRight />
                </button>
              )}
            </div>
          </div>
        );
      }) : (
        <div style={{ fontSize: 13, color: "#065F46" }}>The quote was created in Epicor.</div>
      )}
      {(result.notices || []).length > 0 && (
        <div className="qw-info qw-info-amber" style={{ marginTop: 10, fontSize: 12.5 }}>
          <strong>Warnings</strong>
          <ul style={{ margin: "4px 0 0 18px" }}>
            {result.notices.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}
      {result.error && (
        <div className="qw-info" style={{ marginTop: 10, fontSize: 12.5, background: "#FEF2F2", color: "#991B1B" }}>
          <strong>Not everything was created:</strong> {result.error}
        </div>
      )}
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
        Open a quote to review it and convert it to a sales order.
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={onCreateAnother}><FaPlus /> Create another</button>
      </div>
    </div>
  );
}

export function RollupRow({ label, value, muted, bold }) {
  return (
    <tr>
      <td style={{
        padding: bold ? "6px 0" : "3px 0",
        color: muted ? "#94A3B8" : (bold ? "#0F172A" : "#475569"),
        fontWeight: bold ? 700 : 400,
        fontSize: bold ? 14 : 13,
      }}>
        {label}
      </td>
      <td className="qw-mono" style={{
        padding: bold ? "6px 0" : "3px 0",
        textAlign: "right",
        color: muted ? "#94A3B8" : "#4F46E5",
        fontWeight: bold ? 700 : 600,
        fontSize: bold ? 15 : 13,
      }}>
        {value}
      </td>
    </tr>
  );
}

// (SubmitResultCard removed — replaced by CreatedQuotes, rendered inline in
//  ReviewStep above.)

// (LineCard + PlanRadioCard removed — line building now lives in the one-screen
//  AddLineStep above: SystemSection + buying-option radios + PlanDetailsSection,
//  which reuse PotControls / BkControls / CtoControls /
//  RepDiscountSlider / TradeInBlock / LinePreview defined below.)

// -----------------------------------------------------------------------------
// MSP — multi-shipment sizing + delivery layout.
// Exactly ONE sizing rule travels to the API: a shipment count ("split it into
// N deliveries") or a per-shipment ceiling ("no more than $X at a time"). The
// planner derives the other — sending both would let them disagree, so picking
// a mode clears the other value entirely.
// -----------------------------------------------------------------------------
function MspControls({ line, lineKey, onUpdate }) {
  const pp = line.paymentPlan || {};

  const setMode = (mode) => {
    if (mode === "count") {
      onUpdate({ mspMode: mode, paymentPlan: { planCode: "MSP", shipmentCount: pp.shipmentCount || 3 } });
    } else {
      onUpdate({ mspMode: mode, paymentPlan: { planCode: "MSP", perShipmentCeiling: pp.perShipmentCeiling || 5000 } });
    }
  };

  return (
    <div style={{ background: "#fafbfd", border: "1px solid #e9ecef", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>
        What decides the split?
      </div>
      <div style={{ display: "flex", gap: 20, marginBottom: 12, flexWrap: "wrap" }}>
        <Form.Check
          type="radio"
          name={`msp-mode-${lineKey}`}
          id={`msp-mode-count-${lineKey}`}
          label="Number of shipments"
          checked={line.mspMode !== "ceiling"}
          onChange={() => setMode("count")}
        />
        <Form.Check
          type="radio"
          name={`msp-mode-${lineKey}`}
          id={`msp-mode-ceiling-${lineKey}`}
          label="Per-shipment budget"
          checked={line.mspMode === "ceiling"}
          onChange={() => setMode("ceiling")}
        />
      </div>

      {line.mspMode === "ceiling" ? (
        <div style={{ maxWidth: 280 }}>
          <label className="qw-label" htmlFor={`msp-ceiling-${lineKey}`} style={{ marginBottom: 4 }}>
            Ceiling per shipment ($)
          </label>
          <Form.Control
            id={`msp-ceiling-${lineKey}`}
            type="number"
            min={1}
            step={50}
            value={pp.perShipmentCeiling ?? ""}
            onChange={(e) => onUpdate({
              paymentPlan: { planCode: "MSP", perShipmentCeiling: e.target.value === "" ? "" : Number(e.target.value) },
            })}
          />
          <div style={{ marginTop: 4, fontSize: 11.5, color: "#94A3B8" }}>
            The planner works out how many shipments that needs.
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: 280 }}>
          <label className="qw-label" htmlFor={`msp-count-${lineKey}`} style={{ marginBottom: 4 }}>
            Number of shipments
          </label>
          <Form.Control
            id={`msp-count-${lineKey}`}
            type="number"
            min={MSP_MIN_SHIPMENTS}
            max={MSP_MAX_SHIPMENTS}
            value={pp.shipmentCount ?? ""}
            onChange={(e) => onUpdate({
              paymentPlan: { planCode: "MSP", shipmentCount: e.target.value === "" ? "" : Number(e.target.value) },
            })}
          />
          <div style={{ marginTop: 4, fontSize: 11.5, color: "#94A3B8" }}>
            {MSP_MIN_SHIPMENTS}–{MSP_MAX_SHIPMENTS} deliveries.
          </div>
        </div>
      )}

      <div style={{ height: 1, background: "#E2E8F0", margin: "14px 0" }} />
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>
        How should the customer receive it?
      </div>
      <Form.Check
        type="checkbox"
        id={`msp-sep-quotes-${lineKey}`}
        label="Separate Epicor quote per shipment"
        checked={!!line.mspSeparateQuotes}
        onChange={(e) => onUpdate({ mspSeparateQuotes: e.target.checked })}
      />
      <div style={{ margin: "0 0 8px 24px", fontSize: 11.5, color: "#94A3B8" }}>
        Off: one quote carrying every shipment as its own kit line — the customer
        POs individual lines. On: one ordinary quote per shipment.
      </div>
      <Form.Check
        type="checkbox"
        id={`msp-sep-docs-${lineKey}`}
        label="Separate PDF per shipment"
        checked={!!line.mspSeparateDocs}
        onChange={(e) => onUpdate({ mspSeparateDocs: e.target.checked })}
      />
      <div style={{ margin: "0 0 0 24px", fontSize: 11.5, color: "#94A3B8" }}>
        Stored on the plan and honoured automatically whenever the quote is
        emailed — nobody has to remember it at send time.
      </div>

      <div className="qw-info qw-info-emerald" style={{ marginTop: 12, fontSize: 12 }}>
        Multi-shipment quotes exactly <strong>one system</strong> — quantity doesn't apply.
        Pricing is engineered so every shipment bills the same figure, and each
        shipment is ordered by its own customer PO within a 90-day window.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// POT — radio between "Choose Months" and "Enter Budget".
// Adds APR tier badge (green/amber/red) for the slider mode and surfaces the
// >36-month suggestion the backend returns in budget mode.
// -----------------------------------------------------------------------------
function potAprTier(months) {
  const m = Number(months) || 0;
  if (m <= 12) return { label: "Low APR", bg: "#e6f4ea", color: "#1e7e34" };
  if (m <= 24) return { label: "Mid APR", bg: "#fff3cd", color: "#856404" };
  return { label: "High APR", bg: "#fde2e1", color: "#a32d2d" };
}

function PotControls({ line, lineKey, suggestion, onUpdate }) {
  // Dynamic budget floor — the monthly payment at the 36-month max term for
  // this line's NET price (after system + rep discount, × qty). Anything below
  // it can never amortize, so the budget field clamps to it on blur. The net
  // price comes from the live preview; until one has run there's no floor and
  // the server-side guard (ReverseCalculateAsync) is the backstop.
  const [minMonthly, setMinMonthly] = useState(null);
  const netPrice = Number(line.preview?.priceAfterDiscount) || 0;
  useEffect(() => {
    let cancelled = false;
    if (!netPrice || netPrice <= 0) { setMinMonthly(null); return undefined; }
    fetch(`${API_URL}/api/Sales/pot/minimum-monthly?devicePrice=${netPrice}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const v = Number(data?.minimumMonthly);
        setMinMonthly(Number.isFinite(v) && v > 0 ? v : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [netPrice]);

  const setMode = (mode) => {
    if (mode === "months") {
      onUpdate({ potMode: mode, paymentPlan: { planCode: "POT", numberOfMonths: line.paymentPlan.numberOfMonths || 12 } });
    } else {
      // ONE behaviour (Issue 26 / OBS-28): the budget box is prefilled with the
      // floor — the monthly payment at the 36-month maximum — as soon as that
      // floor is known, and with nothing before. The old fixed $1,000 default
      // appeared whenever the minimum-monthly call had not answered yet, so the
      // same click showed 1000, or the floor, or nothing, depending on timing.
      const floor = minMonthly ? Math.ceil(minMonthly) : null;
      onUpdate({ potMode: mode, paymentPlan: { planCode: "POT", desiredPerInvoice: line.paymentPlan.desiredPerInvoice || floor || undefined } });
    }
  };
  // The floor usually arrives AFTER the rep clicks "Enter Budget" (it waits for
  // the live preview): fill an empty or below-floor budget with it the moment it
  // is known — once — so the box never sits empty or shows a figure the solver
  // will bounce. A budget the rep typed above the floor is left alone.
  const budgetMode = line.potMode === "budget";
  const draftBudget = Number(line.paymentPlan.desiredPerInvoice) || 0;
  useEffect(() => {
    if (!budgetMode || !minMonthly) return;
    const floor = Math.ceil(minMonthly);
    if (draftBudget <= 0 || draftBudget < minMonthly) {
      onUpdate({ paymentPlan: { planCode: "POT", desiredPerInvoice: floor } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetMode, minMonthly]);

  const months = Number(line.paymentPlan.numberOfMonths || 12);
  const tier = potAprTier(months);

  // Measure the slider's ACTUAL rendered width so the tick legend is pinned to the
  // real track — a CSS `100%` on the sibling label row can resolve to a slightly
  // wider box, which left the thumb sitting just left of each label. The thumb is
  // 16px (.qw-range) so its centre travels 8px → (width − 8px); a label for value v
  // sits at 8 + frac·(width − 16), frac = (v − 2)/34.
  const [rangeW, setRangeW] = useState(0);
  const rangeRoRef = useRef(null);
  const setRangeRef = useCallback((el) => {
    if (rangeRoRef.current) { rangeRoRef.current.disconnect(); rangeRoRef.current = null; }
    if (el) {
      setRangeW(el.offsetWidth);
      const ro = new ResizeObserver(() => setRangeW(el.offsetWidth));
      ro.observe(el);
      rangeRoRef.current = ro;
    }
  }, []);

  const applySuggestion = () => {
    if (!suggestion?.suggestedPerInvoice) return;
    onUpdate({
      paymentPlan: { planCode: "POT", desiredPerInvoice: Number(suggestion.suggestedPerInvoice) },
    });
  };

  return (
    <div style={{ background: "#fafbfd", border: "1px solid #e9ecef", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
        <Form.Check
          type="radio"
          name={`pot-mode-${lineKey}`}
          id={`pot-mode-months-${lineKey}`}
          label="Choose Months"
          checked={line.potMode === "months"}
          onChange={() => setMode("months")}
        />
        <Form.Check
          type="radio"
          name={`pot-mode-${lineKey}`}
          id={`pot-mode-budget-${lineKey}`}
          label="Enter Budget"
          checked={line.potMode === "budget"}
          onChange={() => setMode("budget")}
        />
      </div>
      {line.potMode === "months" ? (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
            <span className="fw-semibold">Months: {months}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                background: tier.bg, color: tier.color,
              }}>{tier.label}</span>
              <span style={{ color: "#666" }}>2 → 36</span>
            </span>
          </div>
          <input
            ref={setRangeRef}
            type="range"
            className="qw-range"
            min={2}
            max={36}
            step={1}
            value={months}
            onChange={(e) => onUpdate({ paymentPlan: { planCode: "POT", numberOfMonths: Number(e.target.value) } })}
          />
          {/* Each label is centred (translateX(-50%)) on its TRUE thumb-centre.
              The .qw-range thumb is a known 16px, so its centre travels
              8px → (measuredWidth − 8px); value v sits at 8 + frac·(W − 16),
              frac = (v − 2)/34. Verified against a faithful repro: label centres
              land exactly on the thumb (12 → 252px, 36 → W−8) and "36" stays
              ~2.6px inside the track — the 8px inset pulls the end labels in, so
              −50% centring no longer overflows. */}
          <div style={{ position: "relative", height: 14, fontSize: 10, color: "#999", marginTop: -2 }}>
            {[6, 12, 18, 24, 30, 36].map((v) => {
              const frac = (v - 2) / 34;
              return (
                <span
                  key={v}
                  style={{
                    position: "absolute",
                    left: rangeW
                      ? `${8 + frac * (rangeW - 16)}px`
                      : `calc(8px + ${frac} * (100% - 16px))`,
                    transform: "translateX(-50%)",
                    whiteSpace: "nowrap",
                    margin: 0, // override the global `span { margin-left: 16px }`
                               // rule that otherwise shoves every tick 16px right
                  }}
                >
                  {v}
                </span>
              );
            })}
          </div>
        </div>
      ) : (
        <div>
          <Form.Label className="small fw-semibold mb-1">Customer's monthly budget ($)</Form.Label>
          <Form.Control
            type="number"
            min={minMonthly ? Math.ceil(minMonthly) : 0}
            value={line.paymentPlan.desiredPerInvoice || ""}
            onChange={(e) => onUpdate({ paymentPlan: { planCode: "POT", desiredPerInvoice: Number(e.target.value) } })}
            onBlur={() => {
              // Snap a below-floor budget up to the floor (rounded up to a whole
              // dollar) — mirrors the CTO slider's minimum-plan clamp.
              // UI-QR-N006: a negative budget snaps up too (it was kept and sent).
              const v = Number(line.paymentPlan.desiredPerInvoice);
              if (minMonthly && Number.isFinite(v) && v !== 0 && v < minMonthly) {
                onUpdate({ paymentPlan: { planCode: "POT", desiredPerInvoice: Math.ceil(minMonthly) } });
              }
            }}
            placeholder="e.g. 3000"
          />
          {minMonthly && (
            <div style={{ fontSize: 11, color: "#5b7a99", marginTop: 4 }}>
              Minimum ≈ {fmtMoney(minMonthly)}/month for this price (paid over the 36-month maximum). Lower entries snap up to it.
            </div>
          )}
          {suggestion?.message && (
            <Alert variant="warning" style={{ marginTop: 8, marginBottom: 0, padding: "8px 12px", fontSize: 12 }}>
              <div>{suggestion.message}</div>
              {suggestion.suggestedPerInvoice && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={applySuggestion}
                  style={{ marginTop: 6, fontSize: 11, fontWeight: 600 }}
                >
                  Apply ${Number(suggestion.suggestedPerInvoice).toLocaleString(undefined, { maximumFractionDigits: 2 })}/mo
                </Button>
              )}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// BK — radio quick-picks + custom input.
// -----------------------------------------------------------------------------
function BkControls({ line, lineKey, previewPricing, onUpdate }) {
  const current = line.paymentPlan.desiredPerInvoice;
  const bkMode = line.bkMode || "quick";

  // Slider thumb position — clamp the stored value into [min, max] for display
  // (a custom-entered value outside the range still drives pricing as-is).
  const sliderVal = (() => {
    const n = Number(current);
    if (!Number.isFinite(n) || n <= 0) return 2000;
    return Math.min(BK_SLIDER_MAX, Math.max(BK_SLIDER_MIN, n));
  })();

  const setMode = (mode) => {
    if (mode === "quick") {
      // Snap to the nearest $1,000 step inside the slider range (default $2,000).
      const n = Number(current);
      const snapped = Number.isFinite(n) && n > 0
        ? Math.min(BK_SLIDER_MAX, Math.max(BK_SLIDER_MIN, Math.round(n / BK_SLIDER_STEP) * BK_SLIDER_STEP))
        : 2000;
      onUpdate({ bkMode: mode, paymentPlan: { planCode: "BK", desiredPerInvoice: snapped } });
    } else {
      // Custom: clear desiredPerInvoice so the field reads as empty until the
      // rep types a value. Preview won't fire until a positive amount is set.
      onUpdate({ bkMode: mode, paymentPlan: { planCode: "BK", desiredPerInvoice: null } });
    }
  };

  // Measure the slider's rendered width so the tick legend pins to the real
  // track — same approach as the POT months slider.
  const [rangeW, setRangeW] = useState(0);
  const rangeRoRef = useRef(null);
  const setRangeRef = useCallback((el) => {
    if (rangeRoRef.current) { rangeRoRef.current.disconnect(); rangeRoRef.current = null; }
    if (el) {
      setRangeW(el.offsetWidth);
      const ro = new ResizeObserver(() => setRangeW(el.offsetWidth));
      ro.observe(el);
      rangeRoRef.current = ro;
    }
  }, []);

  return (
    <div style={{ background: "#fafbfd", border: "1px solid #e9ecef", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
        <Form.Check
          type="radio"
          name={`bk-mode-${lineKey}`}
          id={`bk-mode-quick-${lineKey}`}
          label="Quick Picks"
          checked={bkMode === "quick"}
          onChange={() => setMode("quick")}
        />
        <Form.Check
          type="radio"
          name={`bk-mode-${lineKey}`}
          id={`bk-mode-custom-${lineKey}`}
          label="Custom Amount"
          checked={bkMode === "custom"}
          onChange={() => setMode("custom")}
        />
      </div>

      {bkMode === "quick" ? (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
            <span className="fw-semibold">Per-invoice cap: ${sliderVal.toLocaleString()}</span>
            <span style={{ color: "#666" }}>$1k → $9k</span>
          </div>
          <input
            ref={setRangeRef}
            type="range"
            className="qw-range"
            min={BK_SLIDER_MIN}
            max={BK_SLIDER_MAX}
            step={BK_SLIDER_STEP}
            value={sliderVal}
            onChange={(e) => onUpdate({ paymentPlan: { planCode: "BK", desiredPerInvoice: Number(e.target.value) } })}
          />
          {/* Tick legend — centred on the true thumb-centre (.qw-range thumb is
              16px), value v sits at 8 + frac·(W − 16), frac = (v − min)/(max − min). */}
          <div style={{ position: "relative", height: 14, fontSize: 10, color: "#999", marginTop: -2 }}>
            {[1000, 3000, 5000, 7000, 9000].map((v) => {
              const frac = (v - BK_SLIDER_MIN) / (BK_SLIDER_MAX - BK_SLIDER_MIN);
              return (
                <span
                  key={v}
                  style={{
                    position: "absolute",
                    left: rangeW
                      ? `${8 + frac * (rangeW - 16)}px`
                      : `calc(8px + ${frac} * (100% - 16px))`,
                    transform: "translateX(-50%)",
                    whiteSpace: "nowrap",
                    margin: 0,
                  }}
                >
                  ${v / 1000}k
                </span>
              );
            })}
          </div>
        </div>
      ) : (
        <div>
          <Form.Label className="small fw-semibold mb-1">Per-invoice cap ($)</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={current ?? ""}
            onChange={(e) =>
              onUpdate({
                paymentPlan: {
                  planCode: "BK",
                  desiredPerInvoice: e.target.value === "" ? null : Number(e.target.value),
                },
              })
            }
            placeholder="e.g. 7500"
          />
        </div>
      )}

      {previewPricing && Number(previewPricing.numberOfInvoices) > 0 && Number(previewPricing.perInvoiceAmount) > 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: "#0f2742", fontWeight: 600 }}>
          {previewPricing.numberOfInvoices} invoices of {fmtMoney(previewPricing.perInvoiceAmount)} each
        </div>
      )}
      <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>$25 invoice fee per split.</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CTO — radio between Named Plan / Custom Amount.
// -----------------------------------------------------------------------------
// Parse the K-value out of a named CTO plan code, e.g. "C4K" -> 4.
const CTO_K_OF = (code) => { const m = /^C(\d+)K$/.exec(code || ""); return m ? Number(m[1]) : null; };

function CtoControls({ line, lineKey, onUpdate }) {
  // The smallest plan that fits within 36 months, from the preview's own net × qty
  // (UI-QTE-084: the old floor asked with one unit's LIST price). Only trusted when it
  // was priced for this part and quantity.
  const minPlanCode = line.ctoMinKey === ctoMinKeyOf(line) && line.ctoMinPlan
    ? String(line.ctoMinPlan).toUpperCase()
    : null;
  const floorK = CTO_K_OF(minPlanCode);
  const storedK = CTO_K_OF(line.paymentPlan.namedCtoPlan) || 3;

  // The stored plan follows the floor, so the preview prices the plan the label shows
  // (the slider clamps its DISPLAY; without this the line kept C3K under a "$4k" label).
  useEffect(() => {
    if (line.ctoMode !== "named" || !floorK || storedK >= floorK) return;
    onUpdate({ paymentPlan: { planCode: "CTO", namedCtoPlan: `C${Math.min(9, floorK)}K` } });
  }, [line.ctoMode, floorK, storedK, onUpdate]);

  const setMode = (mode) => {
    if (mode === "named") {
      onUpdate({ ctoMode: mode, paymentPlan: { planCode: "CTO", namedCtoPlan: line.paymentPlan.namedCtoPlan || "C3K" } });
    } else {
      onUpdate({ ctoMode: mode, paymentPlan: { planCode: "CTO", customMonthlyAmount: line.paymentPlan.customMonthlyAmount || 2500 } });
    }
  };
  const returnEligibleMonth = line.preview?.returnEligibleMonth ?? line.preview?.ReturnEligibleMonth;
  return (
    <div style={{ background: "#fafbfd", border: "1px solid #e9ecef", borderRadius: 8, padding: "14px 16px" }}>
      <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
        <Form.Check
          type="radio"
          name={`cto-mode-${lineKey}`}
          id={`cto-mode-named-${lineKey}`}
          label="Choose Plan"
          checked={line.ctoMode === "named"}
          onChange={() => setMode("named")}
        />
        <Form.Check
          type="radio"
          name={`cto-mode-${lineKey}`}
          id={`cto-mode-custom-${lineKey}`}
          label="Enter Amount"
          checked={line.ctoMode === "custom"}
          onChange={() => setMode("custom")}
        />
      </div>

      {line.ctoMode === "named" ? (
        (() => {
          // Named ceilings on a $1k–$9k slider (step $1k). Selecting a value sets
          // namedCtoPlan to "C{k}K" — identical to clicking the old C-chips, so the
          // pricing path is unchanged. The minimum-viable-plan floor clamps the low end.
          const minK = Math.min(9, Math.max(1, CTO_K_OF(minPlanCode) || 1));
          const minVal = minK * 1000;
          const curK = Math.min(9, Math.max(minK, CTO_K_OF(line.paymentPlan.namedCtoPlan) || 3));
          const val = curK * 1000;
          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
                <span className="fw-semibold">Plan ceiling: ${val / 1000}k <span style={{ color: "#666", fontWeight: 400 }}>(C{val / 1000}K)</span></span>
                <span style={{ color: "#666" }}>$1k → $9k</span>
              </div>
              <input
                type="range"
                className="qw-range"
                min={1000}
                max={9000}
                step={1000}
                value={val}
                onChange={(e) => {
                  const v = Math.max(minVal, Number(e.target.value));
                  onUpdate({ paymentPlan: { planCode: "CTO", namedCtoPlan: `C${v / 1000}K` } });
                }}
              />
              <div style={{ position: "relative", height: 14, fontSize: 10, color: "#999", marginTop: -2 }}>
                {[1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000].map((v) => {
                  const frac = (v - 1000) / 8000;
                  return (
                    <span key={v} style={{ position: "absolute", left: `calc(8px + ${frac} * (100% - 16px))`, transform: "translateX(-50%)", whiteSpace: "nowrap", margin: 0, opacity: v < minVal ? 0.4 : 1 }}>
                      ${v / 1000}k
                    </span>
                  );
                })}
              </div>
              {/* The "Minimum viable plan" footnote is printed once, under Shipping/Misc (OBS-29). */}
            </div>
          );
        })()
      ) : (
        <div>
          <Form.Label className="small fw-semibold mb-1">Monthly ceiling ($)</Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={line.paymentPlan.customMonthlyAmount || ""}
            onChange={(e) => onUpdate({ paymentPlan: { planCode: "CTO", customMonthlyAmount: Number(e.target.value) } })}
            placeholder="e.g. 2500"
          />
        </div>
      )}

      <Row className="g-3" style={{ marginTop: 12 }}>
        <Col xs={6}>
          <Form.Label className="small fw-semibold mb-1">Shipping ($)</Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={line.shippingAmount || ""}
            onChange={(e) => onUpdate({ shippingAmount: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="0"
          />
        </Col>
        <Col xs={6}>
          <Form.Label className="small fw-semibold mb-1">Misc charges ($)</Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={line.miscChargesAmount || ""}
            onChange={(e) => onUpdate({ miscChargesAmount: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="0"
          />
        </Col>
      </Row>

      {minPlanCode && (
        <div style={{ fontSize: 11, color: "#5b7a99", marginTop: 8 }}>
          Minimum viable plan for this device price: <strong>{minPlanCode}</strong>
        </div>
      )}
      {returnEligibleMonth != null && (
        <div style={{
          marginTop: 10, padding: "8px 10px", borderRadius: 6,
          background: "#e6f4ea", color: "#1e7e34", fontSize: 12, fontWeight: 600,
        }}>
          Return eligible from month {returnEligibleMonth}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Rep discount slider — 0–20%, shared by all plans.
// (RepDiscountSlider removed — rep discounts are an Edit-Quote affordance now,
//  gated on action 00079 for CustomerService / IT.)

// -----------------------------------------------------------------------------
// Trade-in (N30 only).
// -----------------------------------------------------------------------------
// Trade-In (N30 only). Open state is derived directly from the `tradeIn`
// prop so it can never desync from the line: clicking "+ Add trade-in"
// sets a non-null object on the line (parent updates state), and on the
// next render the form is shown. Clicking "remove" sets it back to null.
// Small labelled cell used in the inline trade-in strip. Fixed width so
// the row stays clean when stacked under multiple line cards on a quote.
function TradeInField({ label, width, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "0 0 auto", width }}>
      <Form.Label
        className="mb-1"
        style={{ fontSize: 10, color: "#6c757d", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.3 }}
      >
        {label}
      </Form.Label>
      {children}
    </div>
  );
}

function TradeInBlock({ tradeIn, onUpdate }) {
  const isOpen = tradeIn != null;
  // Notes live behind a toggle so the form is a single horizontal row by
  // default — open the textarea on demand. Auto-opens if there's an
  // existing note so the user can see what was previously entered.
  const [notesOpen, setNotesOpen] = useState(Boolean(tradeIn?.notes));

  if (!isOpen) {
    return null;
  }

  const t = tradeIn;
  const numOrEmpty = (v) => (v === "" ? "" : Number(v));
  const hasNotes = Boolean((t.notes || "").trim());

  return (
    <div
      style={{
        marginTop: 12,
        padding: "8px 12px",
        background: "#fafbfd",
        border: "1px solid #e9ecef",
        borderRadius: 8,
      }}
    >
      {/* Single horizontal strip — leading "Trade-In" stamp, then fields,
          then a notes toggle on the right. flex-wrap so it stays usable
          on narrow screens without disturbing the per-card layout. */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", flex: "0 0 auto", marginRight: 4 }}>
          <span
            style={{
              fontSize: 10,
              color: "#0052A5",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              lineHeight: 1.1,
            }}
          >
            Trade-In
          </span>
          <Button
            variant="link"
            size="sm"
            style={{ padding: 0, fontSize: 11, color: "#dc3545", textAlign: "left" }}
            onClick={() => onUpdate(null)}
          >
            remove
          </Button>
        </div>

        <TradeInField label="Part #" width={140}>
          {/* Same /api/Part/systems/search autocomplete the main line's
              Part Number field uses. hideSecondary keeps the picker tight
              inside the 140px trade-in cell (the part description would
              otherwise spill into the next field). inputWidth=100% lets
              the input fill the cell instead of the component's 14ch
              default. */}
          <AutocompleteField
            size="sm"
            config={{ ...AUTOCOMPLETE_PART, hideSecondary: true }}
            value={t.partNum || ""}
            onChange={(val) => onUpdate({ ...t, partNum: (val || "").toUpperCase() })}
            onSelectItem={(item) => {
              const code = (item.Part_PartNum || item.partNum || "").toUpperCase();
              onUpdate({ ...t, partNum: code });
            }}
            placeholder="S8BLADE"
            inputWidth="100%"
          />
        </TradeInField>

        <TradeInField label="Serial #" width={120}>
          <Form.Control
            size="sm"
            placeholder="—"
            value={t.serialNum || ""}
            onChange={(e) => onUpdate({ ...t, serialNum: e.target.value })}
          />
        </TradeInField>

        <TradeInField label="Condition" width={100}>
          <Form.Select
            size="sm"
            value={t.condition || "Good"}
            onChange={(e) => onUpdate({ ...t, condition: e.target.value })}
          >
            <option>Excellent</option>
            <option>Good</option>
            <option>Fair</option>
            <option>Poor</option>
          </Form.Select>
        </TradeInField>

        <TradeInField label="Selling Price" width={110}>
          <Form.Control
            size="sm"
            type="number"
            min="0"
            step="0.01"
            placeholder="$0.00"
            value={t.estimatedValue ?? ""}
            onChange={(e) => onUpdate({ ...t, estimatedValue: numOrEmpty(e.target.value) })}
          />
        </TradeInField>

        <TradeInField label="Orig. Price" width={110}>
          <Form.Control
            size="sm"
            type="number"
            min="0"
            step="0.01"
            placeholder="$0.00"
            value={t.originalPurchasePrice ?? ""}
            onChange={(e) => onUpdate({ ...t, originalPurchasePrice: numOrEmpty(e.target.value) })}
          />
        </TradeInField>

        <TradeInField label="Disc %" width={80}>
          <Form.Control
            size="sm"
            type="number"
            min="0"
            max="100"
            step="0.01"
            placeholder="10"
            value={t.discountPercent ?? ""}
            onChange={(e) => onUpdate({ ...t, discountPercent: numOrEmpty(e.target.value) })}
          />
        </TradeInField>

        {/* Notes toggle — small, on the right; emphasises when there's a
            value (filled outline) and the full text shows in the title
            tooltip on hover, so the user can read it without expanding. */}
        <Button
          variant={hasNotes ? "primary" : "outline-secondary"}
          size="sm"
          style={{ padding: "0.25rem 0.6rem", fontSize: 12, flex: "0 0 auto" }}
          onClick={() => setNotesOpen((open) => !open)}
          title={hasNotes ? t.notes : "Add a note"}
        >
          {hasNotes ? "📝 Note" : "+ Note"}
        </Button>
      </div>

      {/* Notes expansion — only renders when toggled open (or auto-opened
          because there's an existing value). Tight rows={2} keeps height
          predictable. */}
      {notesOpen && (
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          placeholder="optional notes for the trade-in"
          value={t.notes || ""}
          onChange={(e) => onUpdate({ ...t, notes: e.target.value })}
          style={{ marginTop: 8 }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-line preview status — shown inline under each line in Step 2.
// -----------------------------------------------------------------------------
function LinePreview({ line }) {
  if (!line.partNum) return null;

  // Only show the standalone "Calculating…" spinner on the FIRST calculation,
  // when there's no prior pricing to keep on screen. Once a preview exists, a
  // re-calc (rep discount, plan amount, qty…) keeps the card mounted and just
  // dims + refreshes the values in place — no collapse/expand "page reload".
  if (line.previewLoading && !line.preview) {
    return (
      <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
        <Spinner animation="border" size="sm" style={{ width: 12, height: 12, marginRight: 6 }} />
        Calculating…
      </div>
    );
  }

  // A price contract refused this part for this customer. CreateSalesQuoteHandler
  // throws on the same verdict, so this is a stop — not the amber "pricing
  // unavailable" advisory below, which the rep is allowed to submit past. Add to
  // cart is disabled alongside it (commitDisabled).
  if (isContractBlocked(line)) {
    const why = line.previewError || line.contractNote;
    return (
      <div className="qw-info qw-info-amber" style={{ marginTop: 12, background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}>
        <div>
          <div style={{ fontWeight: 700 }}>This part can't be quoted to this customer.</div>
          <div style={{ marginTop: 4, fontSize: 12.5, lineHeight: 1.45, overflowWrap: "anywhere" }}>{why}</div>
          {line.contractNote && line.contractNote !== why && (
            <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>{line.contractNote}</div>
          )}
          <div style={{ marginTop: 6, fontSize: 11.5 }}>
            Their price contract refuses it, and the quote would be rejected on submit for the
            same reason. Pick a different part, or have the contract amended first.
          </div>
        </div>
      </div>
    );
  }

  // A typed plan amount the API would refuse: say so in plain words instead of the raw
  // server reply ("HTTP 400", "CTO plan 'CTOC' has no InvoiceLimit").
  const amountProblem = planAmountProblem(line);
  if (amountProblem) {
    return (
      <div role="alert" className="qw-info" style={{ marginTop: 12, background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}>
        <div style={{ fontWeight: 700 }}>{amountProblem}</div>
        <div style={{ marginTop: 4, fontSize: 12 }}>Fix the amount above to see the price. Add to cart stays off until then.</div>
      </div>
    );
  }

  if (line.previewError) {
    // Translate the most common raw Epicor errors into something a rep can
    // act on. A missing preview alone does not stop the line, but a part Epicor
    // cannot price does: Add to cart stays disabled (isUnpriced, SAL-GAP-84).
    const raw = String(line.previewError);
    let friendly = raw;
    if (/no header row|GetNewQuoteHed/i.test(raw)) {
      friendly = "Live pricing preview unavailable for this customer/rep/part combination. The quote can still be submitted — pricing will be finalized server-side.";
    } else if (isUnpriced(line)) {
      friendly = "Epicor could not price this part, so it can't be added to the cart yet. Pick another part, or ask Customer Service to set up its price in Epicor.";
    }
    return (
      <Alert variant="warning" style={{ marginTop: 12, padding: "8px 12px", fontSize: 12 }}>
        {friendly}
        {line.previewSuggestion?.message && (
          <div style={{ marginTop: 4, fontStyle: "italic" }}>{line.previewSuggestion.message}</div>
        )}
        {/* The preview failed but the customer is still on a contract — the
            submit will price the line from it, so the rep is told now. */}
        {line.contractNote && (
          <div style={{ marginTop: 6, fontSize: 11.5, overflowWrap: "anywhere" }}>
            <strong>Contract price · </strong>
            <span className="qw-mono">{line.contractNote}</span>
          </div>
        )}
        {friendly !== raw && (
          <div style={{ marginTop: 4, fontSize: 11, color: "#888" }}>Detail: {raw}</div>
        )}
      </Alert>
    );
  }

  const p = line.preview;
  if (!p) return null;

  // A re-calc is in flight but we already have pricing on screen — keep the card
  // mounted and just dim the numbers while the new ones arrive (set below).
  const loading = line.previewLoading;

  // When reorder parts ride along, this card's total is only the SYSTEM portion
  // — the line's grand total (system + reorder) is shown by ReorderPartsSummary
  // below. Relabel + de-emphasize so it reads as a subtotal, not the headline.
  const hasReorder = selectedReorderBoms(line).length > 0;

  // Full breakdown — same fields as the Step 3 Pricing Summary, rendered
  // per-line on Step 2 so reps can audit each line's pricing inline without
  // navigating to Review.
  // A multi-shipment preview carries shipments[] — a shipment IS an invoice
  // there, so numberOfInvoices/perInvoiceAmount read as shipments.
  const isMsp = Array.isArray(p.shipments) && p.shipments.length > 0;
  const perInvoiceLabel =
    p.perInvoiceAmount !== null && p.perInvoiceAmount !== undefined
      ? (p.numberOfMonths
          ? `${p.numberOfMonths}× monthly`
          : isMsp
            ? `${p.numberOfInvoices || ""} shipments`
            : `${p.numberOfInvoices || ""} invoices`)
      : null;

  // Single-row strip — only includes cells whose value is meaningful.
  // Zeros are filtered out so the row reads as a compact summary of just
  // the components that actually move the price (Selling, then any
  // discounts/fees that are populated, then Tax + Total). Stays readable
  // when multiple line cards are stacked because each line gets one row
  // instead of a 9-row table.
  const nz = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n !== 0;
  };
  // Cost components (zeros filtered) rendered as a tidy label/value list with
  // the Total pulled out into the header. Discounts read muted/negative.
  // Every figure below is EXTENDED (whole-line), not per unit. With the quantity
  // absent from this card, "$43,050.00" on a qty-2 line reads as a unit price and
  // the numbers look wrong. Show the qty in the header and spell out the unit math
  // on the two rows that scale with it.
  // A multi-shipment preview is ONE system split across shipments - never a
  // per-unit figure, whatever the draft says (Issue 23 / DEFECT-36).
  const previewQty = isMsp ? 1 : Math.max(1, Number(line.quantity) || 1);
  const showUnitMath = previewQty > 1;
  const unitOf = (total) => fmtMoney(Number(total) / previewQty);

  const rows = [];
  if (nz(p.sellingPrice)) {
    rows.push({
      label: showUnitMath ? `Selling price · ${previewQty} × ${unitOf(p.sellingPrice)}` : "Selling price",
      value: fmtMoney(p.sellingPrice),
    });
  }
  if (nz(p.systemDiscount)) {
    rows.push({
      label: showUnitMath ? `System discount · ${previewQty} × ${unitOf(p.systemDiscount)}` : "System discount",
      value: `(${fmtMoney(p.systemDiscount)})`,
      neg: true,
    });
  }
  if (nz(p.repDiscountAmount))rows.push({ label: "Rep discount",    value: `(${fmtMoney(p.repDiscountAmount)})`, neg: true });
  if (nz(p.tariffAmount))     rows.push({ label: "Tariff",          value: fmtMoney(p.tariffAmount) });
  if (nz(p.setupFee))         rows.push({ label: "Setup fee",       value: fmtMoney(p.setupFee) });
  if (nz(p.interestAmount))   rows.push({ label: "Interest",        value: fmtMoney(p.interestAmount) });
  if (nz(p.totalInvoiceFees)) rows.push({ label: "Invoice fees",    value: fmtMoney(p.totalInvoiceFees) });
  // The Consign-to-Own extras the rep typed (Shipping + Misc). The API has already
  // folded them into the monthly and the total the way the finished quote does
  // (Issue 37); this row only says how much of each month they are.
  if (nz(p.customerExtras)) {
    rows.push({
      label: nz(p.extrasPerMonth) && p.numberOfMonths
        ? `Shipping + misc \u00b7 ${fmtMoney(p.extrasPerMonth)} \u00d7 ${p.numberOfMonths} months`
        : "Shipping + misc",
      value: fmtMoney(p.customerExtras),
    });
  }
  // Issue 38 (Kapil: "show both monthlies"): a Consign-to-Own preview carries a second
  // monthly with the estimated sales tax spread over the months the way the finished
  // quote spreads Epicor's real tax. Both are shown; the first is labelled pre-tax.
  const hasTaxedMonthly = nz(p.perInvoiceAmountWithEstimatedTax);
  if (perInvoiceLabel && nz(p.perInvoiceAmount)) {
    rows.push({ label: hasTaxedMonthly ? `${perInvoiceLabel} (pre-tax)` : perInvoiceLabel, value: fmtMoney(p.perInvoiceAmount) });
  }
  if (perInvoiceLabel && hasTaxedMonthly) {
    rows.push({
      label: `${perInvoiceLabel} incl. est. tax ${fmtMoney(p.estimatedTaxAmount)}`,
      value: fmtMoney(p.perInvoiceAmountWithEstimatedTax),
    });
  }
  if (nz(p.interestRate))     rows.push({ label: "Interest (APR)",  value: fmtPct(Number(p.interestRate) * 100), muted: true });
  // The trade-in credit (Issue 25 / OBS-34). The API now says what it will post
  // as the STID "Trade In Discount" charge; it sits OUTSIDE the line price, so it
  // is shown under the total as what comes off, never folded into it.
  const tradeInCredit = Number(p.tradeInValue) || 0;
  const afterTradeIn = Number(p.totalPrice || 0) - Number(p.taxAmount || 0) - tradeInCredit;

  return (
    <div style={{ marginTop: 12, border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
      {/* Header — mellowed: just the label + part #. The total moved to the
          footer row below so it reads as the sum of the breakdown, and the
          neutral styling lets this card sit calmly beside the reorder parts. */}
      <div style={{ padding: "8px 14px", background: "#F8FAFC", borderBottom: "1px solid #EEF2F7" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "#94A3B8" }}>Pricing preview</span>
          {loading && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "#6366F1", fontWeight: 700 }}>
              <Spinner animation="border" size="sm" style={{ width: 10, height: 10, borderWidth: 2 }} /> Updating…
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span className="qw-mono" style={{ fontWeight: 700, color: "#334155", fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line.partNum}</span>
          {/* Quantity belongs next to the part: these totals are for the whole
              line, and without it a qty-2 figure reads as a unit price. */}
          {showUnitMath && (
            <span className="qw-mono" style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: "#4338CA", background: "#EEF2FF", border: "1px solid #E0E7FF", borderRadius: 999, padding: "1px 8px" }}>
              ×{previewQty}
            </span>
          )}
          {/* (Plan-code pill hidden for now, along with the plan-name banner —
              the buying-option radios already tell the rep what they picked.) */}
        </div>
      </div>
      {/* Breakdown — one row per component */}
      <div style={{ padding: "6px 14px 8px", opacity: loading ? 0.45 : 1, transition: "opacity .15s ease" }}>
        {rows.map((r, i) => (
          <div key={r.label} style={{
            display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12,
            padding: "5px 0", borderTop: i === 0 ? "none" : "1px solid #f1f5f9",
          }}>
            <span style={{ fontSize: 12.5, color: r.muted ? "#94A3B8" : "#64748B" }}>{r.label}</span>
            <span className="qw-mono" style={{ fontSize: 13, fontWeight: 600, color: r.neg ? "#B91C1C" : (r.muted ? "#94A3B8" : "#1f2937") }}>{r.value}</span>
          </div>
        ))}
      </div>
      {/* Contract pricing — WHY this line's figures differ from the catalogue.
          The server sends one pre-formatted sentence (list, reduction, percent,
          net, contract id); it is rendered verbatim, never re-computed here. */}
      {line.contractNote && (
        <div className="qw-info-indigo" style={{ padding: "8px 14px", borderTop: "1px solid #E0E7FF", opacity: loading ? 0.45 : 1, transition: "opacity .15s ease" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 2 }}>Contract price</div>
          <div className="qw-mono" style={{ fontSize: 11.5, lineHeight: 1.45, overflowWrap: "anywhere" }}>{line.contractNote}</div>
        </div>
      )}
      {/* Multi-shipment: the exact per-delivery figures. Deliveries are not
          always equal (rounding lands on individual shipments), and each is
          purchase-ordered separately, so the exact figure per shipment matters
          — the "PO must say" column is what the customer's PO must match. */}
      {isMsp && (
        <div style={{ padding: "4px 14px 10px", opacity: loading ? 0.45 : 1, transition: "opacity .15s ease" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr>
                {["#", "Product", "Fee", "PO must say"].map((h, i) => (
                  <th key={h} style={{
                    textAlign: i === 0 ? "left" : "right", fontSize: 10, textTransform: "uppercase",
                    letterSpacing: 0.4, color: "#94A3B8", padding: "4px 0", borderBottom: "1px solid #E2E8F0",
                    fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {p.shipments.map((s) => (
                <tr key={s.sequence}>
                  <td className="qw-mono" style={{ padding: "3px 0", color: "#64748B" }}>{s.sequence}</td>
                  <td className="qw-mono" style={{ padding: "3px 0", textAlign: "right", color: "#475569" }}>{fmtMoney(s.productAmount)}</td>
                  <td className="qw-mono" style={{ padding: "3px 0", textAlign: "right", color: "#475569" }}>{s.fee ? fmtMoney(s.fee) : "—"}</td>
                  <td className="qw-mono" style={{ padding: "3px 0", textAlign: "right", fontWeight: 700, color: "#4F46E5" }}>{fmtMoney(s.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 6, fontSize: 11, color: "#94A3B8", lineHeight: 1.4 }}>
            Each shipment is ordered by its own customer PO for exactly the
            "PO must say" figure — a PO for part of a shipment converts nothing.
          </div>
        </div>
      )}
      {/* Total footer — the sum of the breakdown above. Reads as "System subtotal"
          when reorder parts ride along (the line's grand total is the band below). */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
        padding: "9px 14px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC",
        opacity: loading ? 0.45 : 1, transition: "opacity .15s ease",
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>{hasReorder ? "System subtotal" : "Total"}</span>
        <span className="qw-mono" style={{ fontWeight: 700, color: "#0052A5", fontSize: hasReorder ? 16 : 18, lineHeight: 1.1 }}>{fmtMoney(Number(p.totalPrice || 0) - Number(p.taxAmount || 0))}</span>
      </div>
      {tradeInCredit > 0 && (
        <div style={{ padding: "6px 14px 8px", borderTop: "1px solid #EEF2F7", background: "#FFFBEB", opacity: loading ? 0.45 : 1, transition: "opacity .15s ease" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "3px 0" }}>
            <span style={{ fontSize: 12.5, color: "#92400E" }}>Trade-in credit ({fmtPct(Number(p.tradeInPercent) || 0)} of list)</span>
            <span className="qw-mono" style={{ fontSize: 13, fontWeight: 600, color: "#B91C1C" }}>({fmtMoney(tradeInCredit)})</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "3px 0" }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>After trade-in (pre-tax)</span>
            <span className="qw-mono" style={{ fontSize: 14, fontWeight: 700, color: "#0052A5" }}>{fmtMoney(afterTradeIn)}</span>
          </div>
          <div style={{ fontSize: 11, color: "#92400E", lineHeight: 1.4 }}>
            Posted on the quote as a separate "Trade In Discount" charge, so the quote total above stays the system price.
          </div>
        </div>
      )}
      <div style={{ padding: "8px 14px", borderTop: "1px solid #EEF2F7", background: "#fff", fontSize: 11, color: "#64748B", lineHeight: 1.4 }}>
        Pre-tax estimate (matches the Epicor quote).{nz(p.taxAmount) ? ` Est. sales tax ${fmtMoney(p.taxAmount)} is applied when the quote becomes an order.` : ""} May still change based on shipping, tariffs, and other charges at submission.
      </div>
    </div>
  );
}

export default QuoteCreate;
